// Bilingual content for the entire public website.
// Long-form prose lives here so pages can render either language seamlessly.

export const translations = {
  fr: {
    locale: "fr",
    common: {
      languageName: "Français",
      switchTo: "EN",
      readMore: "Lire la suite",
      back: "Retour",
      loading: "Chargement…",
      submit: "Envoyer",
      support: "Soutenir",
      join: "Adhérer",
      donate: "Faire un don",
      bookTickets: "Réserver",
      free: "Entrée libre",
      euro: "€",
    },
    nav: {
      home: "Accueil",
      documents: "Documents",
      events: "Événements",
      offices: "Offices",
      concerts: "Concerts",
      conferences: "Conférences",
      agenda: "Agenda",
      autres: "Autres",
      presence: "Fêtes populaires de saint Léonard",
      association: "Association",
      mecenat: "Mécénat",
      contact: "Contact",
      admin: "Admin",
    },
    home: {
      kicker: "Vains · Baie du Mont-Saint-Michel",
      heroTitle: "Prieuré saint Léonard",
      heroSubtitle: "Sur un promontoire dominant la baie, depuis les Mérovingiens…",
      heroTagline: "Là où l'expérience sensorielle du Profane rejoint le Sacré.",
      introTitle: "L'âme des lieux est empreinte de paix, d'écoute et de liberté intérieure.",
      introBody: [
        "Entre ciel et grèves, dernière pierre avant la mer et première promesse du Mont,",
        "À la croisée de la route des Ducs et de la Via Angelica venant du Monte Sant'Angelo, des voix sacrées s'élèvent,",
        "Du Levant au Couchant comme la course de la vie,",
        "Depuis l'Orient, au prieuré, saint Léonard nous libère de toutes nos prisons avant la traversée des eaux de la Mort vers la Jérusalem Céleste,",
        "Vers l'Occident, au mont saint Michel, l'Archange soupèse nos âmes et nous met sur le chemin de la Résurrection.",
      ],
      historyKicker: "L'histoire",
      historyTitle: "Du « Lion de la Forêt » à la renaissance contemporaine",
      historyChapters: [
        {
          title: "578 — Leodovald, futur saint Léonard",
          body: "Né en 540 dans une famille franque, proche de Clovis, chargé des prisonniers du royaume de France, Leodovald — le « Lion de la Forêt » — devient premier évêque d'Avranches en 578. Il finit ermite en Limousin et donne son nom à cette terre habitée depuis la préhistoire par des populations de pêcheurs cueilleurs. Sa dévotion se répand dans toute l'Europe ; il devient le saint patron des prisonniers.",
        },
        {
          title: "1087 — Donation de Guillaume le Conquérant",
          body: "Né en 1027 à Falaise et mort le 9 septembre 1087 à Rouen, Guillaume le Conquérant — dont le fief comportait un manoir, des salines, des activités de pêche et de varech — donne à sa mort, en 1087, le domaine aux bénédictins de l'abbaye saint Étienne de Caen, dite « Abbaye aux Hommes ». Les moines y érigent un prieuré. Le lieu devient la dernière halte des miquelots vers le Mont saint Michel — attesté depuis 1025.",
        },
        {
          title: "XVIIᵉ siècle — Le déclin",
          body: "La commende marque le déclin du prieuré au XVIIᵉ siècle, la communauté monastique quittant les lieux désormais entretenus par un agriculteur local. Devenu bien national sous la Révolution, un particulier en fait l'acquisition et le transforme en exploitation agricole.",
        },
        {
          title: "1944–1970 — Reprise et classement",
          body: "Pendant la seconde guerre, la Wehrmacht installe un observatoire au sommet de l'ancien clocher. En juin 1944, la famille Poilvillain de Misouard reprend les lieux avec le souci de respecter le don du duc normand aux moines bénédictins de Caen. Le prieuré est classé Monument Historique en janvier 1970.",
        },
        {
          title: "1975 → aujourd'hui — La renaissance",
          body: "Dès 1975, le projet est porté de faire renaître et vivre cet écrin dans l'esprit de ses racines. Les travaux de restauration sont lancés dans les années 80. L'ancienne ferme est démolie, laissant place à la chapelle. Transformé en habitation, le clocher est rétabli. La statue vénérée de saint Léonard est restaurée.",
        },
      ],
      prayerTitle: "Prière de saint Léonard",
      prayerLines: [
        "« Saint Léonard, libère-moi de tout lien mauvais et donne-moi la force de la Charité. »",
        "« Donne-nous de voir les liens qui nous enchaînent et les relations qui nous libèrent. »",
      ],
      featuredArticleKicker: "Actualité",
      featuredArticleTitle: "Un baptême émouvant",
      featuredArticleBody:
        "Le 29 août 2024, la cloche sainte Anne — après des pérégrinations entre la Marine Nationale, le Maroc et la Normandie — a trouvé son nom et sa vocation au prieuré.",
      eventsKicker: "Agenda",
      eventsTitle: "Les prochaines rencontres",
      eventsCta: "Voir tous les événements",
      presenceKicker: "Millenium 2027",
      presenceTitle: "PRESENCE — Fêtes populaires de saint Léonard",
      presenceBody:
        "Du 1ᵉʳ juillet 17 h 00 au 5 juillet 11 h 00 2027 : cinq jours de Libération, Lumière et Unité. Conférences, ostensions, procession vers le Mont, jumelage avec l'abbaye San Leonardo de Foggia.",
      presenceCta: "Découvrir & s'inscrire",
      vivaKicker: "VIVA ARTIST · Label de production",
      vivaTitle: "Un projet à confier ?",
      vivaBody:
        "Musique, chant, cinéma, image, installation — VIVA ARTIST est le label du Prieuré. Il accueille les artistes qui sculptent la lumière et le souffle pour nous faire vibrer d'éternité.",
      vivaCta: "Découvrir le label",
      supportKicker: "Soutenir",
      supportTitle: "Faites vivre le Prieuré",
      supportBody:
        "Adhérez aux Amis du Prieuré ou contribuez aux projets de restauration en cours. Chaque geste prolonge mille ans d'histoire.",
    },
    documents: {
      kicker: "Archives",
      title: "Documents & ressources",
      intro:
        "Vous trouverez ici un fonds documentaire, iconographique, musical voire vidéo permettant d'approfondir la connaissance des lieux et la compréhension de son écosystème. Nous sommes à l'écoute de toute suggestion d'ajout pertinente.",
      categories: {
        all: "Tous",
        history: "Histoire & Archives",
        patrimony: "Patrimoine & Plans",
        association: "Vie de l'Association",
        millenium: "Fêtes Millenium 2027",
      },
      featured: "À la une",
      searchPlaceholder: "Rechercher un document…",
      empty: "Aucun document dans cette catégorie pour l'instant.",
      open: "Consulter",
      download: "Télécharger",
      downloads: "consultations",
      downloadsOne: "consultation",
      formats: {
        pdf: "PDF", image: "Image", audio: "Audio", video: "Vidéo",
        doc: "Word", docx: "Word", xls: "Excel", xlsx: "Excel",
        ppt: "Powerpoint", pptx: "Powerpoint", file: "Fichier",
      },
    },
    events: {
      kicker: "Agenda",
      title: "Événements à venir",
      empty: "Aucun événement à venir pour le moment. Revenez bientôt.",
      details: "Détails & réservation",
      ticketedTag: "Sur réservation",
      freeTag: "Entrée libre",
      atTime: "à",
      subscribeIcal: "S'abonner au calendrier",
      icalHint: "Importez cet agenda dans Google Calendar, Apple Calendar ou Outlook.",
    },
    eventsByCategory: {
      office: { kicker: "Vie liturgique", title: "Offices à venir", empty: "Aucun office programmé pour l'instant. Revenez bientôt." },
      concert: { kicker: "Musique", title: "Concerts à venir", empty: "Aucun concert programmé pour l'instant. Revenez bientôt." },
      conference: { kicker: "Rencontres", title: "Conférences à venir", empty: "Aucune conférence programmée pour l'instant. Revenez bientôt." },
      autre: { kicker: "Agenda", title: "Autres événements", empty: "Aucun autre événement pour l'instant. Revenez bientôt." },
    },
    eventDetail: {
      bookTitle: "Réserver mes places",
      quantity: "Nombre de places",
      fullName: "Nom complet",
      email: "Adresse e-mail",
      total: "Total",
      payNow: "Payer en ligne",
      free: "Entrée libre — pas de réservation nécessaire.",
    },
    association: {
      kicker: "Rejoignez-nous",
      title: "L'association des Amis du Prieuré",
      mission: "L'association des Amis du Prieuré saint Léonard a pour objet de :",
      missions: [
        "Valoriser le patrimoine culturel, artistique et spirituel du prieuré saint Léonard et ses environs par l'organisation de toute manifestation ou initiative en cohérence avec l'esprit des lieux et ouverte au plus large public,",
        "Développer toute activité de recherche ou d'études scientifiques liée à ce lieu,",
        "Contribuer à l'entretien de la chapelle et de ses abords.",
      ],
      membershipNote:
        "L'adhésion est valable 12 mois à compter de la date de souscription. Les habitants de la commune de Vains bénéficient du tarif réduit. L'association étant d'intérêt général, les adhésions et dons ouvrent droit à un reçu fiscal (réduction d'impôt de 66 % dans la limite de 20 % du revenu imposable, art. 200 du CGI).",
      tiersTitle: "Formules d'adhésion",
      tierLabels: {
        plein: "Plein tarif",
        reduit: "Tarif réduit",
        reduitNote: "artistes, vainquais, chômeurs, étudiants, retraités",
        couple: "Couple",
        coupleNote: "deux adhérents au même foyer",
        scouts_guides: "Scouts & Guides",
        scoutsGuidesNote: "une adhésion par patrouille, équipe ou clan",
        paroissiens_avranches: "Paroissiens d'Avranches",
        paroissiensAvranchesNote: "tarif de proximité, doyenné d'Avranches",
        libre: "Don libre",
        libreNote: "Montant à votre convenance — défiscalisé",
      },
      formTitle: "Vos coordonnées",
      fullName: "Nom complet",
      email: "Adresse e-mail",
      freeAmount: "Montant (€)",
      proceedJoin: "Procéder à l'adhésion",
      proceedDonate: "Procéder au don",
    },
    contact: {
      kicker: "Nous écrire",
      title: "Contact",
      intro:
        "Une question, une suggestion, une demande de visite ou de partenariat ? Écrivez-nous. Nous répondons dans les meilleurs délais.",
      name: "Nom complet",
      email: "Adresse e-mail",
      phone: "Téléphone (optionnel)",
      subject: "Sujet",
      message: "Votre message",
      send: "Envoyer le message",
      success: "Merci, votre message a bien été transmis.",
      direct: "Vous pouvez également nous écrire directement :",
      phoneLabel: "Téléphone",
      addressLabel: "Adresse",
    },
    payment: {
      checking: "Vérification du paiement…",
      successTitle: "Paiement confirmé",
      successBody: "Merci de votre soutien. Vous recevrez bientôt une confirmation par e-mail.",
      pendingTitle: "Paiement en cours de traitement",
      pendingBody: "Nous vérifions le statut de votre transaction.",
      failedTitle: "Paiement non finalisé",
      failedBody: "Le paiement n'a pas pu être confirmé. Vous pouvez réessayer.",
      backHome: "Retour à l'accueil",
    },
    presence: {
      kicker: "Millenium 2027 · Année Européenne des Normands",
      title: "PRESENCE",
      tagline: "Là où l'expérience sensorielle du Profane rejoint le Sacré.",
      intro:
        "PRESENCE est le projet artistique, patrimonial et culturel phare du Prieuré saint Léonard, inscrit dans le programme Millenium 2027 porté par la Région Normandie. Il réactive la mémoire de la donation de 1087 par Guillaume le Conquérant, célèbre la figure de saint Léonard et tisse une coopération européenne autour des routes normandes.",
      pillarsTitle: "Quatre piliers",
      pillars: [
        { title: "Fresques médiévales", body: "Création de fresques inspirées des techniques du XIIᵉ siècle : le Pantocrator, le domaine ducal de Vains, la donation de 1087, saint Benoît patron de l'Europe, saint Léonard, Bohémond Iᵉʳ d'Antioche." },
        { title: "Ostensions contemporaines", body: "Temps forts publics revisitant la tradition septennale des ostensions de saint Léonard — inscrite au patrimoine UNESCO — ouvertes aux anciens prisonniers et aux pèlerins." },
        { title: "Médiation & ateliers", body: "Conférences universitaires, rencontres avec les artistes et chercheurs, ateliers de fresque, d'icône, de chant et balades botaniques médiévales." },
        { title: "Rencontres européennes", body: "Rencontres et coopération avec l'abbaye San Leonardo di Siponto (Foggia, Italie), reconnexion du réseau des lieux normands d'Europe (Sicile, Angleterre, Italie du Nord)." },
      ],
      eventTitle: "Les Fêtes populaires de saint Léonard — du 1er juillet 17 h au 5 juillet 11 h 2027",
      eventSubtitle: "Trois jours, trois thèmes : Libération · Lumière · Unité",
      fresquesKicker: "Création artistique",
      fresquesTitle: "Les fresques, cœur du projet",
      fresquesBody:
        "Inspirées des pigments retrouvés dans la chapelle et des techniques romanes du XIIᵉ siècle, les fresques de PRESENCE inscrivent durablement le récit dans la pierre. Conçues avec des artistes et chercheurs français et italiens, elles ouvrent un dialogue entre l'art médiéval et la sensibilité contemporaine.",
      fresquesQuote: "« Inscrire le récit dans la pierre. »",
      fresquesStudies: "Études · 2025—2027",
      fresquesCta: "Découvrir la création",
      fresquesThemes: [
        "Le Pantocrator",
        "Le domaine ducal de Vains",
        "La donation de 1087",
        "Saint Benoît, patron de l'Europe",
        "Saint Léonard",
        "Bohémond Iᵉʳ d'Antioche",
      ],
      unescoBadge: "Patrimoine UNESCO",
      unescoTitle: "Les Ostensions de saint Léonard",
      unescoBody:
        "Les Ostensions septennales — tradition vivante depuis le XIᵉ siècle — sont inscrites au patrimoine culturel immatériel de l'humanité par l'UNESCO depuis 2013. Tous les sept ans, les reliques de saint Léonard sortent du prieuré pour être vénérées par les pèlerins, anciens prisonniers et fidèles venus de toute l'Europe. Les Fêtes populaires de saint Léonard de 2027 marquent la prochaine grande ostension.",
      days: [
        {
          name: "Vendredi 2 juillet",
          theme: "LA LIBÉRATION",
          color: "cinnabar",
          schedule: [
            { time: "10h00", place: "Avranches", item: "Accueil officiel & conférence inaugurale : saint Léonard, libérateur et patron des Normands" },
            { time: "12h30", place: "Avranches", item: "Buffet convivial" },
            { time: "14h30", place: "Prieuré de Vains", item: "Ateliers au choix — icône, sculpture / modelage, chants, balade botanique (plantes médicinales médiévales)" },
            { time: "18h30", place: "Prieuré de Vains", item: "Vêpres" },
            { time: "20h00", place: "Prieuré de Vains", item: "Dîner & veillée : pièce sur saint Léonard, témoignages, restitution des ateliers" },
          ],
        },
        {
          name: "Samedi 3 juillet",
          theme: "LA LUMIÈRE",
          color: "gold",
          schedule: [
            { time: "10h00", place: "Prieuré de Vains", item: "Messe solennelle, suivie des ostensions des reliques de saint Léonard dans le village" },
            { time: "13h00", place: "Prieuré ou bergerie", item: "Déjeuner" },
            { time: "15h00", place: "Baie du Mont saint Michel", item: "Procession vers le Mont, portage des reliques, stations spirituelles, présence des confréries" },
            { time: "19h00", place: "Mont saint Michel", item: "Célébration dans l'église abbatiale, dîner & veillée de vénération" },
          ],
        },
        {
          name: "Dimanche 4 juillet",
          theme: "L'UNITÉ",
          color: "lapis",
          schedule: [
            { time: "10h30", place: "Prieuré de Vains", item: "Messe solennelle de clôture, bénédiction des fresques, installation d'une relique de Monte San'Angelo" },
            { time: "12h00", place: "Prieuré de Vains", item: "Cérémonie de jumelage avec l'abbaye San Leonardo de Foggia (Italie)" },
            { time: "13h00", place: "Prieuré de Vains", item: "Déjeuner festif" },
            { time: "15h00", place: "Prieuré de Vains", item: "Envoi" },
          ],
        },
      ],
      preRegKicker: "Sans engagement",
      preRegTitle: "Pré-inscription sans engagement",
      preRegBody:
        "Manifestez votre intérêt dès maintenant pour les Fêtes populaires de saint Léonard. Nous reviendrons vers vous avec les modalités définitives et les détails pratiques. Aucun paiement n'est requis à ce stade.",
      preRegFields: {
        nbPeople: "Nombre de personnes",
        country: "Pays",
        organization: "ORGANISATION (OPTIONNEL)",
        accommodation: "Hébergement souhaité",
        accommodationOptions: {
          habitant: "Accueil chez l'habitant",
          hotel: "Hôtel",
          congregation: "Congrégation",
          gite: "Gîte",
          tente: "Sous tente",
          aucun: "Aucun (je me débrouille)",
        },
        interestDays: "Journées d'intérêt",
        notes: "Précisions (régime alimentaire, accessibilité, attentes…)",
        submit: "Envoyer ma pré-inscription",
        success: "Merci ! Votre pré-inscription est enregistrée. Nous vous tiendrons informé(e).",
      },
      formulaLabels: {
        jour: "Journée découverte",
        jourNote: "Un seul jour au choix",
        pass3j: "Pass 3 jours",
        pass3jNote: "Hors repas",
        pass3j_repas: "Pass 3 jours + repas",
        pass3j_repasNote: "Tous les repas inclus",
        solidaire: "Tarif solidaire",
        solidaireNote: "Étudiants, demandeurs d'emploi, Vainquais",
      },
      dayOptions: { "2027-07-02": "Vendredi 2 (Libération)", "2027-07-03": "Samedi 3 (Lumière)", "2027-07-04": "Dimanche 4 (Unité)" },
      workshopLabel: "Atelier souhaité (vendredi)",
      workshopOptions: { icone: "Icône", sculpture: "Sculpture / modelage", chants: "Chants", botanique: "Balade botanique" },
      quantity: "Nombre de personnes",
      name: "Nom complet",
      email: "Adresse e-mail",
      phone: "Téléphone",
      notes: "Notes (régime alimentaire, accessibilité…)",
      total: "Total",
      proceed: "Procéder au paiement",
      contactBlock: "Pour toute question sur PRESENCE :",
      contactName: "Auriane de Potesta",
    },
    footer: {
      tagline: "Sur la voie montoise, à la croisée de la route des Ducs et de la Via Angelica.",
      contact: "Contact",
      address: "Prieuré saint Léonard, 50300 Vains, France",
      navTitle: "Le site",
      legal: "© 2026 Les Amis du Prieuré saint Léonard. Association loi 1901 reconnue d'intérêt général.",
      legalNotice: "Mentions légales",
      privacy: "Confidentialité",
    },
    admin: {
      loginTitle: "Espace administrateur",
      loginSubtitle: "Accès réservé au Conseil d'Administration",
      email: "Adresse e-mail",
      password: "Mot de passe",
      login: "Se connecter",
      logout: "Déconnexion",
      tabs: {
        dashboard: "Tableau de bord",
        events: "Événements",
        articles: "Actualités",
        messages: "Messages",
        orders: "Inscriptions / Paiements",
        presence: "Fêtes 2027",
        members: "Membres",
        logistics: "Logistique",
      },
      stats: {
        events: "Événements",
        articles: "Articles",
        unread: "Messages non lus",
        orders: "Paiements reçus",
        revenue: "Recettes",
      },
      newEvent: "Nouvel événement",
      newArticle: "Nouvel article",
      save: "Enregistrer",
      cancel: "Annuler",
      edit: "Modifier",
      delete: "Supprimer",
      confirmDelete: "Confirmer la suppression ?",
      eventFields: {
        titleFr: "Titre (FR)",
        titleEn: "Titre (EN)",
        descFr: "Description (FR)",
        descEn: "Description (EN)",
        date: "Date",
        time: "Heure",
        locFr: "Lieu (FR)",
        locEn: "Lieu (EN)",
        image: "URL de l'image",
        ticketed: "Billetterie payante",
        price: "Prix (€)",
        capacity: "Capacité",
        published: "Publié",
      },
      articleFields: {
        titleFr: "Titre (FR)",
        titleEn: "Titre (EN)",
        bodyFr: "Corps (FR)",
        bodyEn: "Corps (EN)",
        image: "URL de l'image",
        author: "Auteur",
        date: "Date",
        published: "Publié",
      },
      noMessages: "Aucun message pour le moment.",
      noOrders: "Aucune inscription pour le moment.",
      markRead: "Marquer comme lu",
    },
    ui: {
      preRegistration: "PRÉ-INSCRIPTION", openMenu: "Ouvrir le menu", close: "Fermer",
      mySpace: "Mon espace", logout: "Déconnexion", signIn: "SE CONNECTER", register: "S'INSCRIRE",
      backToHome: "Retour à l'accueil", friendsOfPriory: "Amis du Prieuré", resources: "Bibliothèque",
      newsletterUnsubscribeTitle: "Désinscription",
      newsletterNoEmail: "Aucun email fourni. Vérifiez le lien depuis votre boîte mail.",
      newsletterBusy: "Traitement en cours…",
      newsletterOk: "✓ Désinscription confirmée",
      newsletterOkBody: "L'adresse {email} ne recevra plus de communications. Vous pouvez vous réinscrire à tout moment depuis la page des Fêtes 2027.",
      newsletterFail: "Erreur lors de la désinscription. Contactez-nous pour assistance.",
      origin: "Aux origines des Fêtes", programme: "Programme", preRegistrationShort: "Pré-inscription",
      registration: "Inscription", artists: "Passeurs de Lumière", youthMeetings: "Rencontres européennes des jeunes",
      becomeVolunteer: "Devenir bénévole", bookletFeasts: "Passages — Le Livret des Fêtes",
      usefulLinks: "Bon à savoir", contacts: "Contacts",
      passeurs: "Passeurs de la Baie",
      passeursFoi: "Passeurs de l'Indicible",
      passeursHistoireSciences: "Passeurs de l'histoire et des sciences",
      partners: "Partenaires",
      gallery: "le Prieuré s'éveille…",
    },
  },

  en: {
    locale: "en",
    common: {
      languageName: "English",
      switchTo: "FR",
      readMore: "Read more",
      back: "Back",
      loading: "Loading…",
      submit: "Send",
      support: "Support",
      join: "Join",
      donate: "Donate",
      bookTickets: "Book",
      free: "Free entry",
      euro: "€",
    },
    nav: {
      home: "Home",
      documents: "Archives",
      events: "Events",
      offices: "Offices",
      concerts: "Concerts",
      conferences: "Lectures",
      agenda: "Calendar",
      autres: "Other",
      presence: "Popular Feasts of Saint Léonard",
      association: "Association",
      mecenat: "Patronage",
      contact: "Contact",
      admin: "Admin",
    },
    home: {
      kicker: "Vains · Mont-Saint-Michel Bay",
      heroTitle: "The Priory of Saint Léonard",
      heroSubtitle: "On a promontory above the bay, since the Merovingians…",
      heroTagline: "Where the sensory experience of the Profane meets the Sacred.",
      introTitle: "The soul of this place is steeped in peace, listening and inner freedom.",
      introBody: [
        "Between sky and sandbanks, the last stone before the sea and the first promise of the Mount,",
        "At the crossroads of the Way of the Dukes and the Via Angelica from Monte Sant'Angelo, sacred voices rise,",
        "From East to West, like the course of a life,",
        "From the East, at the priory, Saint Leonard frees us from all our prisons before the crossing of the waters of Death towards the Heavenly Jerusalem,",
        "Towards the West, at Mont-Saint-Michel, the Archangel weighs our souls and sets us on the path of Resurrection.",
      ],
      historyKicker: "History",
      historyTitle: "From the 'Lion of the Forest' to a contemporary rebirth",
      historyChapters: [
        {
          title: "578 — Leodovald, the future Saint Léonard",
          body: "Born in 540 to a Frankish family close to Clovis and entrusted with the prisoners of the kingdom of France, Leodovald — the 'Lion of the Forest' — became the first bishop of Avranches in 578. He ended his life as a hermit in Limousin and gave his name to this land, inhabited since prehistory by fisher-gatherers. His devotion spread across Europe and he became the patron saint of prisoners.",
        },
        {
          title: "1087 — William the Conqueror's donation",
          body: "Born in 1027 in Falaise and died on 9 September 1087 in Rouen, William the Conqueror — whose fief comprised a manor, salt marshes, fishing and seaweed harvesting — gave the estate upon his death, in 1087, to the Benedictines of the abbey of Saint Étienne in Caen, the 'Abbey of Men'. The monks built a priory there. The site became the last stop for pilgrims to Mont Saint Michel — attested since 1025.",
        },
        {
          title: "17th century — Decline",
          body: "The commendam system marked the decline of the priory in the 17th century; the monastic community left the site, which was then maintained by a local farmer. Confiscated as a national property during the Revolution, it was acquired by a private owner and turned into a farm.",
        },
        {
          title: "1944–1970 — Revival and listing",
          body: "During the Second World War, the Wehrmacht installed an observatory atop the former bell tower. In June 1944, the Poilvillain de Misouard family took over the site, anxious to honour the Norman duke's gift to the Benedictines. The priory was listed as a Historical Monument in January 1970.",
        },
        {
          title: "1975 → today — The rebirth",
          body: "From 1975 onward, a project was born to bring the place back to life in the spirit of its roots. Restoration began in the 1980s. The old farm was demolished, giving way to the chapel; the bell tower, turned into a dwelling, was restored; the venerated statue of Saint Léonard was restored.",
        },
      ],
      prayerTitle: "Prayer to Saint Léonard",
      prayerLines: [
        "“Saint Léonard, free me from every evil bond and give me the strength of Charity.”",
        "“Grant us to see the bonds that chain us and the bonds that set us free.”",
      ],
      featuredArticleKicker: "News",
      featuredArticleTitle: "A moving baptism",
      featuredArticleBody:
        "On 29 August 2024, the bell Saint Anne — after journeys between the French Navy, Morocco and Normandy — found her name and her vocation at the priory.",
      eventsKicker: "Calendar",
      eventsTitle: "Upcoming gatherings",
      eventsCta: "See all events",
      presenceKicker: "Millenium 2027",
      presenceTitle: "PRESENCE — Saint Léonard Festivities",
      presenceBody:
        "From 1 July 5.00 pm to 5 July 11.00 am 2027: five days of Liberation, Light and Unity. Conferences, ostensions, procession to the Mount, twinning with the abbey of San Leonardo of Foggia.",
      presenceCta: "Discover & register",
      vivaKicker: "VIVA ARTIST · Production label",
      vivaTitle: "A project to entrust?",
      vivaBody:
        "Music, song, cinema, image, installation — VIVA ARTIST is the priory's label. It welcomes artists who carve light and breath to make us tremble with eternity.",
      vivaCta: "Discover the label",
      supportKicker: "Support",
      supportTitle: "Keep the Priory alive",
      supportBody:
        "Become a member of the Friends of the Priory or contribute to ongoing restoration projects. Every gesture extends a thousand years of history.",
    },
    documents: {
      kicker: "Archives",
      title: "Documents & resources",
      intro:
        "Here you will find documentary, iconographic, musical and video resources to deepen knowledge of the place and its ecosystem. We welcome any relevant suggestion.",
      categories: {
        all: "All",
        history: "History & Archives",
        patrimony: "Heritage & Maps",
        association: "Association life",
        millenium: "Millenium 2027 Festivities",
      },
      featured: "Featured",
      searchPlaceholder: "Search a document…",
      empty: "No document in this category yet.",
      open: "View",
      download: "Download",
      downloads: "views",
      downloadsOne: "view",
      formats: {
        pdf: "PDF", image: "Image", audio: "Audio", video: "Video",
        doc: "Word", docx: "Word", xls: "Excel", xlsx: "Excel",
        ppt: "Powerpoint", pptx: "Powerpoint", file: "File",
      },
    },
    events: {
      kicker: "Calendar",
      title: "Upcoming events",
      empty: "No upcoming event yet. Please come back soon.",
      details: "Details & booking",
      ticketedTag: "Booking required",
      freeTag: "Free entry",
      atTime: "at",
      subscribeIcal: "Subscribe to calendar",
      icalHint: "Import this feed into Google Calendar, Apple Calendar or Outlook.",
    },
    eventsByCategory: {
      office: { kicker: "Liturgical life", title: "Upcoming offices", empty: "No office scheduled yet. Please come back soon." },
      concert: { kicker: "Music", title: "Upcoming concerts", empty: "No concert scheduled yet. Please come back soon." },
      conference: { kicker: "Encounters", title: "Upcoming lectures", empty: "No lecture scheduled yet. Please come back soon." },
      autre: { kicker: "Calendar", title: "Other events", empty: "No other event yet. Please come back soon." },
    },
    eventDetail: {
      bookTitle: "Book your seats",
      quantity: "Number of seats",
      fullName: "Full name",
      email: "Email address",
      total: "Total",
      payNow: "Pay online",
      free: "Free entry — no booking required.",
    },
    association: {
      kicker: "Join us",
      title: "The Friends of the Priory",
      mission: "The Friends of the Priory of Saint Léonard exists to:",
      missions: [
        "Promote the cultural, artistic and spiritual heritage of the Priory of Saint Léonard and its surroundings, through events and initiatives consistent with the spirit of the place and open to the broadest public;",
        "Develop scientific research and studies linked to this place;",
        "Contribute to the upkeep of the chapel and its surroundings.",
      ],
      membershipNote:
        "Membership is valid 12 months from the date of subscription. Residents of the village of Vains benefit from the reduced rate. As an association of general interest, memberships and donations entitle you to a French tax receipt (66% income tax reduction within 20% of taxable income, Art. 200 of the French Tax Code).",
      tiersTitle: "Membership tiers",
      tierLabels: {
        plein: "Full rate",
        reduit: "Reduced rate",
        reduitNote: "artists, Vains residents, jobseekers, students, retirees",
        couple: "Couple",
        coupleNote: "two members in the same household",
        scouts_guides: "Scouts & Guides",
        scoutsGuidesNote: "one membership per patrol, team or clan",
        paroissiens_avranches: "Parishioners of Avranches",
        paroissiensAvranchesNote: "local tier, Avranches deanery",
        libre: "Free donation",
        libreNote: "Amount of your choice — tax-deductible",
      },
      formTitle: "Your details",
      fullName: "Full name",
      email: "Email address",
      freeAmount: "Amount (€)",
      proceedJoin: "Proceed to membership",
      proceedDonate: "Proceed to donation",
    },
    contact: {
      kicker: "Write to us",
      title: "Contact",
      intro:
        "A question, a suggestion, a visit or partnership request? Write to us — we reply as soon as possible.",
      name: "Full name",
      email: "Email address",
      phone: "Phone (optional)",
      subject: "Subject",
      message: "Your message",
      send: "Send message",
      success: "Thank you — your message has been sent.",
      direct: "You can also reach us directly:",
      phoneLabel: "Phone",
      addressLabel: "Address",
    },
    payment: {
      checking: "Verifying payment…",
      successTitle: "Payment confirmed",
      successBody: "Thank you for your support. A confirmation will be emailed to you shortly.",
      pendingTitle: "Payment processing",
      pendingBody: "We are checking your transaction status.",
      failedTitle: "Payment not completed",
      failedBody: "The payment could not be confirmed. You can try again.",
      backHome: "Back to home",
    },
    presence: {
      kicker: "Millenium 2027 · European Year of the Normans",
      title: "PRESENCE",
      tagline: "Where the sensory experience of the Profane meets the Sacred.",
      intro:
        "PRESENCE is the flagship artistic, heritage and cultural project of the Priory of Saint Léonard, part of the Millenium 2027 programme led by the Normandy Region. It reactivates the memory of William the Conqueror's 1087 donation, celebrates Saint Léonard, and weaves European cooperation around the Norman routes.",
      pillarsTitle: "Four pillars",
      pillars: [
        { title: "Medieval frescoes", body: "Frescoes inspired by 12th-century techniques: the Pantocrator, the ducal estate of Vains, the 1087 donation, Saint Benedict patron of Europe, Saint Léonard, Bohemond I of Antioch." },
        { title: "Contemporary ostensions", body: "Public events revisiting the septennial tradition of Saint Léonard's ostensions — a UNESCO heritage — open to former prisoners and pilgrims." },
        { title: "Mediation & workshops", body: "Academic conferences, encounters with artists and researchers, workshops in fresco, icon, chant, and medieval botanical walks." },
        { title: "European encounters", body: "Encounters and cooperation with the abbey San Leonardo di Siponto (Foggia, Italy); reconnection of the European network of Norman places (Sicily, England, Northern Italy)." },
      ],
      eventTitle: "The Popular Feasts of Saint Léonard — 1 July 5 pm → 5 July 11 am · 2027",
      eventSubtitle: "Three days, three themes: Liberation · Light · Unity",
      fresquesKicker: "Artistic creation",
      fresquesTitle: "The frescoes, heart of the project",
      fresquesBody:
        "Inspired by the pigments found in the chapel and the Romanesque techniques of the 12th century, the frescoes of PRESENCE inscribe the narrative durably into stone. Designed with French and Italian artists and researchers, they open a dialogue between medieval art and contemporary sensibility.",
      fresquesQuote: "“To inscribe the story into stone.”",
      fresquesStudies: "Studies · 2025—2027",
      fresquesCta: "Discover the creation",
      fresquesThemes: [
        "The Pantocrator",
        "The ducal estate of Vains",
        "The 1087 donation",
        "Saint Benedict, patron of Europe",
        "Saint Léonard",
        "Bohemond I of Antioch",
      ],
      unescoBadge: "UNESCO Heritage",
      unescoTitle: "The Ostensions of Saint Léonard",
      unescoBody:
        "The septennial Ostensions — a living tradition since the 11th century — were inscribed in 2013 on the UNESCO list of the Intangible Cultural Heritage of Humanity. Every seven years, the relics of Saint Léonard leave the priory to be venerated by pilgrims, former prisoners and faithful from all over Europe. The Popular Feasts of Saint Léonard 2027 mark the next great Ostension.",
      days: [
        {
          name: "Friday 2 July",
          theme: "LIBERATION",
          color: "cinnabar",
          schedule: [
            { time: "10:00", place: "Avranches", item: "Official welcome & opening conference: Saint Léonard, liberator and patron of the Normans" },
            { time: "12:30", place: "Avranches", item: "Convivial buffet" },
            { time: "14:30", place: "Priory of Vains", item: "Workshops to choose from — icon, sculpture / modelling, chant, botanical walk (medieval medicinal plants)" },
            { time: "18:30", place: "Priory of Vains", item: "Vespers" },
            { time: "20:00", place: "Priory of Vains", item: "Dinner & evening vigil: play on Saint Léonard, testimonies, workshop sharing" },
          ],
        },
        {
          name: "Saturday 3 July",
          theme: "LIGHT",
          color: "gold",
          schedule: [
            { time: "10:00", place: "Priory of Vains", item: "Solemn Mass, followed by the ostensions of Saint Léonard's relics in the village" },
            { time: "13:00", place: "Priory or sheepfold", item: "Lunch" },
            { time: "15:00", place: "Mont Saint-Michel Bay", item: "Procession to the Mount, relics carried, spiritual stations, brotherhoods present" },
            { time: "19:00", place: "Mont Saint-Michel", item: "Celebration in the abbey church, dinner & vigil of veneration" },
          ],
        },
        {
          name: "Sunday 4 July",
          theme: "UNITY",
          color: "lapis",
          schedule: [
            { time: "10:30", place: "Priory of Vains", item: "Closing Mass, blessing of the frescoes, installation of a relic from Monte San'Angelo" },
            { time: "12:00", place: "Priory of Vains", item: "Twinning ceremony with the abbey of San Leonardo of Foggia (Italy)" },
            { time: "13:00", place: "Priory of Vains", item: "Festive lunch" },
            { time: "15:00", place: "Priory of Vains", item: "Closing" },
          ],
        },
      ],
      preRegKicker: "No commitment",
      preRegTitle: "Pre-registration — no commitment",
      preRegBody:
        "Express your interest now for the Saint Léonard Festivities. We will come back to you with the final terms and practical details. No payment required at this stage.",
      preRegFields: {
        nbPeople: "Number of people",
        country: "Country",
        organization: "ORGANISATION (OPTIONAL)",
        accommodation: "Preferred accommodation",
        accommodationOptions: {
          habitant: "Home hosting",
          hotel: "Hotel",
          congregation: "Religious community",
          gite: "Gîte",
          tente: "Tent (camping)",
          aucun: "None (I'll manage on my own)",
        },
        interestDays: "Days of interest",
        notes: "Notes (dietary, accessibility, expectations…)",
        submit: "Send my pre-registration",
        success: "Thank you! Your pre-registration is recorded. We'll keep you informed.",
      },
      formulaLabels: {
        jour: "Day pass",
        jourNote: "One day of your choice",
        pass3j: "3-day pass",
        pass3jNote: "Meals not included",
        pass3j_repas: "3-day pass + meals",
        pass3j_repasNote: "All meals included",
        solidaire: "Reduced rate",
        solidaireNote: "Students, jobseekers, Vains residents",
      },
      dayOptions: { "2027-07-02": "Friday 2 (Liberation)", "2027-07-03": "Saturday 3 (Light)", "2027-07-04": "Sunday 4 (Unity)" },
      workshopLabel: "Preferred workshop (Friday)",
      workshopOptions: { icone: "Icon", sculpture: "Sculpture / modelling", chants: "Chant", botanique: "Botanical walk" },
      quantity: "Number of people",
      name: "Full name",
      email: "Email address",
      phone: "Phone",
      notes: "Notes (dietary, accessibility…)",
      total: "Total",
      proceed: "Proceed to payment",
      contactBlock: "For any question about PRESENCE:",
      contactName: "Auriane de Potesta",
    },
    footer: {
      tagline: "On the pilgrim's way, at the crossroads of the Dukes' route and the Via Angelica.",
      contact: "Contact",
      address: "Prieuré saint Léonard, 50300 Vains, France",
      navTitle: "Site",
      legal: "© 2026 The Friends of the Priory of Saint Léonard. Non-profit association under French law (1901), recognised as serving the public interest.",
      legalNotice: "Legal notice",
      privacy: "Privacy",
    },
    admin: {
      loginTitle: "Administrator area",
      loginSubtitle: "Restricted to the Board",
      email: "Email address",
      password: "Password",
      login: "Sign in",
      logout: "Sign out",
      tabs: {
        dashboard: "Dashboard",
        events: "Events",
        articles: "News",
        messages: "Messages",
        orders: "Bookings / Payments",
        presence: "Feasts 2027",
        members: "Members",
        logistics: "Logistics",
      },
      stats: {
        events: "Events",
        articles: "Articles",
        unread: "Unread messages",
        orders: "Paid orders",
        revenue: "Revenue",
      },
      newEvent: "New event",
      newArticle: "New article",
      save: "Save",
      cancel: "Cancel",
      edit: "Edit",
      delete: "Delete",
      confirmDelete: "Confirm deletion?",
      eventFields: {
        titleFr: "Title (FR)",
        titleEn: "Title (EN)",
        descFr: "Description (FR)",
        descEn: "Description (EN)",
        date: "Date",
        time: "Time",
        locFr: "Location (FR)",
        locEn: "Location (EN)",
        image: "Image URL",
        ticketed: "Paid ticketing",
        price: "Price (€)",
        capacity: "Capacity",
        published: "Published",
      },
      articleFields: {
        titleFr: "Title (FR)",
        titleEn: "Title (EN)",
        bodyFr: "Body (FR)",
        bodyEn: "Body (EN)",
        image: "Image URL",
        author: "Author",
        date: "Date",
        published: "Published",
      },
      noMessages: "No messages yet.",
      noOrders: "No bookings yet.",
      markRead: "Mark as read",
    },
    ui: {
      preRegistration: "PRE-REGISTRATION", openMenu: "Open menu", close: "Close",
      mySpace: "My space", logout: "Logout", signIn: "SIGN IN", register: "REGISTER",
      backToHome: "Back to home", friendsOfPriory: "Friends of the Priory", resources: "Library",
      newsletterUnsubscribeTitle: "Unsubscribe",
      newsletterNoEmail: "No email provided. Please check the link from your inbox.",
      newsletterBusy: "Processing…",
      newsletterOk: "✓ Unsubscription confirmed",
      newsletterOkBody: "The address {email} will no longer receive communications. You can re-subscribe anytime from the Feasts 2027 page.",
      newsletterFail: "Error while unsubscribing. Contact us for assistance.",
      origin: "Origins of the Feasts", programme: "Programme", preRegistrationShort: "Pre-registration",
      registration: "Registration", artists: "Bearers of Light", youthMeetings: "European Youth Encounters",
      becomeVolunteer: "Become a volunteer", bookletFeasts: "Passages — The Feasts Booklet",
      usefulLinks: "Good to know", contacts: "Contacts",
      passeurs: "Bearers of the Bay",
      passeursFoi: "Bearers of the Ineffable",
      passeursHistoireSciences: "Bearers of History & Sciences",
      partners: "Partners",
      gallery: "le Prieuré s'éveille…",
    },
  },
};

export const pickLocalized = (obj, locale) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[locale] || obj.fr || obj.en || "";
};

// ---- Partial translations (IT/ES/DE). Missing keys fall back to FR via I18nProvider ----

translations.it = {
  locale: "it",
  common: { switchTo: "EN", readMore: "Continua", back: "Indietro", loading: "Caricamento…",
    submit: "Invia", support: "Sostieni", join: "Aderisci", donate: "Dona", bookTickets: "Prenota",
    free: "Ingresso libero", euro: "€" },
  nav: { home: "Home", documents: "Documenti", events: "Eventi", offices: "Uffici", concerts: "Concerti", conferences: "Conferenze", autres: "Altro", agenda: "Agenda", presence: "Feste di San Leonardo",
    association: "Associazione", mecenat: "Mecenatismo", contact: "Contatti", admin: "Admin" },
  home: {
    kicker: "Vains · Baia di Mont-Saint-Michel",
    heroTitle: "Priorato di San Leonardo",
    heroSubtitle: "Su un promontorio sopra la baia, dai Merovingi…",
    heroTagline: "Dove l'esperienza sensibile del Profano incontra il Sacro.",
    introTitle: "L'anima dei luoghi è intrisa di pace, ascolto e libertà interiore.",
    introBody: [
      "Tra cielo e arene, ultima pietra prima del mare e prima promessa del Monte,",
      "All'incrocio della Via dei Duchi e della Via Angelica proveniente dal Monte Sant'Angelo, voci sacre si levano,",
      "Dal Levante al Ponente, come il corso della vita,",
      "Dall'Oriente, al priorato, san Leonardo ci libera da tutte le nostre prigioni prima della traversata delle acque della Morte verso la Gerusalemme Celeste,",
      "Verso l'Occidente, al Monte Saint-Michel, l'Arcangelo soppesa le nostre anime e ci mette sul cammino della Resurrezione.",
    ],
    historyKicker: "Storia",
    historyTitle: "Dal « Leone della Foresta » alla rinascita contemporanea",
    historyChapters: [
      { title: "578 — Leodovaldo, futuro san Leonardo",
        body: "Nato nel 540 in una famiglia franca, vicino a Clodoveo, incaricato dei prigionieri del regno di Francia, Leodovaldo — il « Leone della Foresta » — diventa primo vescovo di Avranches nel 578. Termina la vita come eremita in Limosino e dà il suo nome a questa terra abitata fin dalla preistoria. La sua devozione si diffonde in tutta Europa; diventa il santo patrono dei prigionieri." },
      { title: "1087 — Donazione di Guglielmo il Conquistatore",
        body: "Nato nel 1027 a Falaise e morto il 9 settembre 1087 a Rouen, Guglielmo il Conquistatore dona alla sua morte, nel 1087, il dominio ai benedettini dell'abbazia di Saint-Étienne di Caen, detta « Abbaye aux Hommes ». I monaci vi edificano un priorato. Il luogo diventa l'ultima tappa dei pellegrini verso il Mont Saint-Michel — attestato dal 1025." },
      { title: "XVII secolo — Il declino",
        body: "La commenda segna il declino del priorato nel XVII secolo, la comunità monastica lascia i luoghi ormai mantenuti da un agricoltore locale. Divenuto bene nazionale durante la Rivoluzione, un privato lo acquista e lo trasforma in azienda agricola." },
      { title: "1944–1970 — Ripresa e classificazione",
        body: "Durante la seconda guerra mondiale, la Wehrmacht installa un osservatorio in cima all'antico campanile. Nel giugno 1944, la famiglia Poilvillain de Misouard riprende i luoghi nel rispetto del dono del duca normanno ai benedettini di Caen. Il priorato è classificato Monumento Storico nel gennaio 1970." },
      { title: "1975 → oggi — La rinascita",
        body: "Dal 1975, nasce il progetto di far rinascere e vivere questo scrigno nello spirito delle sue radici. I lavori di restauro sono lanciati negli anni '80. L'antica fattoria è demolita e lascia il posto alla cappella. Il campanile, trasformato in abitazione, è ripristinato. La statua venerata di san Leonardo è restaurata." },
    ],
    prayerTitle: "Preghiera a san Leonardo",
    prayerLines: [
      "« San Leonardo, liberami da ogni legame cattivo e dammi la forza della Carità. »",
      "« Donaci di vedere i legami che ci incatenano e le relazioni che ci liberano. »",
    ],
    featuredArticleKicker: "Attualità",
    featuredArticleTitle: "Un battesimo commovente",
    featuredArticleBody:
      "Il 29 agosto 2024, la campana sant'Anna — dopo le peregrinazioni tra la Marina francese, il Marocco e la Normandia — ha trovato il suo nome e la sua vocazione al priorato.",
    eventsKicker: "Agenda", eventsTitle: "Prossimi appuntamenti", eventsCta: "Vedi tutti gli eventi",
    presenceKicker: "Millenium 2027", presenceTitle: "PRESENCE — Festa di San Leonardo",
    presenceBody: "Dal 2 al 4 luglio 2027 : tre giorni di Liberazione, Luce e Unità. Conferenze, ostensioni, processione verso il Monte, gemellaggio con l'abbazia San Leonardo di Foggia.",
    presenceCta: "Scopri & iscriviti",
    supportKicker: "Sostieni", supportTitle: "Fai vivere il Priorato",
    supportBody: "Aderisci agli Amici del Priorato o contribuisci ai progetti di restauro in corso. Ogni gesto prolunga mille anni di storia.",
  },
  documents: {
    kicker: "Archivi", title: "Documenti & risorse",
    intro: "Qui troverai un fondo documentario, iconografico, musicale e video per approfondire la conoscenza del luogo e del suo ecosistema. Siamo a disposizione per ogni suggerimento.",
    categories: { all: "Tutti", history: "Storia & Archivi", patrimony: "Patrimonio & Mappe", association: "Vita dell'Associazione", millenium: "Feste Millenium 2027" },
    featured: "In evidenza", searchPlaceholder: "Cerca un documento…", empty: "Nessun documento in questa categoria per ora.",
    open: "Consulta", download: "Scarica", downloads: "visualizzazioni", downloadsOne: "visualizzazione",
  },
  events: { kicker: "Agenda", title: "Eventi in arrivo", empty: "Nessun evento in arrivo per ora.",
    details: "Dettagli & prenotazione", ticketedTag: "Su prenotazione", freeTag: "Ingresso libero", atTime: "alle" },
  eventsByCategory: {
    office: { kicker: "Vita liturgica", title: "Uffici in arrivo", empty: "Nessun ufficio in programma per ora." },
    concert: { kicker: "Musica", title: "Concerti in arrivo", empty: "Nessun concerto in programma per ora." },
    conference: { kicker: "Incontri", title: "Conferenze in arrivo", empty: "Nessuna conferenza in programma per ora." },
    autre: { kicker: "Agenda", title: "Altri eventi", empty: "Nessun altro evento per ora." },
  },
  eventDetail: { bookTitle: "Prenota i tuoi posti", quantity: "Numero di posti", fullName: "Nome completo",
    email: "Indirizzo e-mail", total: "Totale", payNow: "Paga online", free: "Ingresso libero — nessuna prenotazione necessaria." },
  association: {
    kicker: "Aderisci", title: "Gli Amici del Priorato",
    mission: "L'Associazione « Gli Amici del Priorato di San Leonardo » si propone di :",
    missions: [
      "Promuovere il patrimonio culturale, artistico e spirituale del Priorato e del suo ambiente, attraverso eventi e iniziative in armonia con lo spirito del luogo e aperte al pubblico più ampio;",
      "Sviluppare ricerche scientifiche e studi legati a questo luogo;",
      "Contribuire al mantenimento della cappella e dei suoi dintorni.",
    ],
    membershipNote: "L'adesione è valida 12 mesi dalla data di sottoscrizione. Gli abitanti del villaggio di Vains beneficiano della tariffa ridotta.",
    tiersTitle: "Quote di adesione",
    tierLabels: { plein: "Tariffa intera", reduit: "Tariffa ridotta", reduitNote: "artisti, abitanti di Vains, disoccupati, studenti, pensionati",
      couple: "Coppia", coupleNote: "due aderenti dello stesso nucleo familiare",
      scouts_guides: "Scout & Guide", scoutsGuidesNote: "una sola iscrizione per pattuglia, squadra o clan",
      libre: "Donazione libera", libreNote: "Importo a tua scelta" },
    formTitle: "I tuoi dati", fullName: "Nome completo", email: "Indirizzo e-mail",
    freeAmount: "Importo (€)", proceedJoin: "Procedi all'adesione", proceedDonate: "Procedi alla donazione",
  },
  contact: { kicker: "Scrivici", title: "Contatti",
    intro: "Una domanda, un suggerimento, una visita o una richiesta di partenariato? Scrivici — rispondiamo al più presto.",
    name: "Nome completo", email: "Indirizzo e-mail", phone: "Telefono (opzionale)",
    subject: "Oggetto", message: "Messaggio", send: "Invia messaggio",
    success: "Grazie — il tuo messaggio è stato inviato.", direct: "Puoi contattarci anche direttamente:",
    phoneLabel: "Telefono", addressLabel: "Indirizzo" },
  payment: { checking: "Verifica del pagamento…", successTitle: "Pagamento confermato",
    successBody: "Grazie per il tuo sostegno. A breve riceverai una conferma via e-mail.",
    pendingTitle: "Pagamento in elaborazione", pendingBody: "Stiamo verificando lo stato della tua transazione.",
    failedTitle: "Pagamento non completato", failedBody: "Il pagamento non ha potuto essere confermato. Puoi riprovare.",
    backHome: "Torna alla home" },
  presence: {
    kicker: "Millenium 2027 · Anno europeo dei Normanni", title: "PRESENCE",
    tagline: "Dove l'esperienza sensibile del Profano incontra il Sacro.",
    intro: "PRESENCE è il progetto artistico, patrimoniale e culturale di punta del Priorato di San Leonardo, parte del programma Millenium 2027 della Regione Normandia. Riattiva la memoria della donazione del 1087 di Guglielmo il Conquistatore, celebra san Leonardo e tesse cooperazioni europee attorno alle vie normanne.",
    pillarsTitle: "Quattro pilastri",
    pillars: [
      { title: "Affreschi medievali", body: "Affreschi ispirati alle tecniche del XII secolo : il Pantocratore, il dominio ducale di Vains, la donazione del 1087, san Benedetto patrono d'Europa, san Leonardo, Boemondo d'Antiochia." },
      { title: "Ostensioni contemporanee", body: "Eventi pubblici che rivisitano la tradizione settennale delle ostensioni di san Leonardo — patrimonio UNESCO — aperti agli ex prigionieri e ai pellegrini." },
      { title: "Mediazione & laboratori", body: "Conferenze accademiche, incontri con artisti e ricercatori, laboratori di affresco, icona, canto, passeggiate botaniche medievali." },
      { title: "Incontri europei", body: "Incontri e cooperazione con l'abbazia di San Leonardo di Siponto (Foggia, Italia) ; ricollegamento della rete europea dei luoghi normanni (Sicilia, Inghilterra, Italia del Nord)." },
    ],
    eventTitle: "Feste popolari di San Leonardo — dal 1º luglio ore 17 al 5 luglio ore 11 del 2027",
    eventSubtitle: "Tre giorni, tre temi : Liberazione · Luce · Unità",
    fresquesKicker: "Creazione artistica", fresquesTitle: "Gli affreschi, cuore del progetto",
    fresquesBody: "Ispirati ai pigmenti ritrovati nella cappella e alle tecniche romaniche del XII secolo, gli affreschi di PRESENCE iscrivono il racconto in modo duraturo nella pietra. Progettati con artisti e ricercatori francesi e italiani, aprono un dialogo tra arte medievale e sensibilità contemporanea.",
    fresquesQuote: "« Iscrivere la storia nella pietra. »",
    fresquesStudies: "Studi · 2025—2027", fresquesCta: "Scopri la creazione",
    fresquesThemes: ["Il Pantocratore", "Il dominio ducale di Vains", "La donazione del 1087", "San Benedetto, patrono d'Europa", "San Leonardo", "Boemondo I di Antiochia"],
    unescoBadge: "Patrimonio UNESCO", unescoTitle: "Le Ostensioni di San Leonardo",
    unescoBody: "Le Ostensioni settennali — tradizione viva dall'XI secolo — sono state iscritte nel 2013 sulla lista UNESCO del Patrimonio Culturale Immateriale dell'Umanità. Ogni sette anni, le reliquie di san Leonardo escono dal priorato per essere venerate dai pellegrini, dagli ex prigionieri e dai fedeli di tutta Europa. Le Feste 2027 segnano la prossima grande Ostensione.",
    preRegKicker: "Senza impegno", preRegTitle: "Pre-iscrizione — senza impegno",
    preRegBody: "Esprimi il tuo interesse fin d'ora per le Feste di San Leonardo. Ti ricontatteremo con le modalità definitive e i dettagli pratici. Nessun pagamento richiesto in questa fase.",
    contactBlock: "Per ogni domanda su PRESENCE:", contactName: "Auriane de Potesta",
  },
  footer: {
    tagline: "Sulla via dei pellegrini, all'incrocio della rotta dei Duchi e della Via Angelica.",
    contact: "Contatti", address: "Prieuré saint Léonard, 50300 Vains, France",
    navTitle: "Sito", legal: "© 2026 Amici del Priorato di San Leonardo. Associazione di interesse pubblico (Legge 1901).",
    legalNotice: "Note legali", privacy: "Privacy",
  },
  ui: {
    preRegistration: "PRE-ISCRIZIONE", openMenu: "Apri il menu", close: "Chiudi",
    mySpace: "Il mio spazio", logout: "Esci", signIn: "ACCEDI", register: "REGISTRATI",
    backToHome: "Ritorno alla home", friendsOfPriory: "Amici del Priorato", resources: "Biblioteca",
    newsletterUnsubscribeTitle: "Cancellazione", newsletterNoEmail: "Nessuna e-mail fornita. Verifica il link dalla tua casella di posta.",
    newsletterBusy: "Elaborazione in corso…", newsletterOk: "✓ Cancellazione confermata",
    newsletterOkBody: "L'indirizzo {email} non riceverà più comunicazioni. Puoi reiscriverti in qualsiasi momento dalla pagina delle Feste 2027.",
    newsletterFail: "Errore durante la cancellazione. Contattaci per assistenza.",
    origin: "Alle origini delle Feste", programme: "Programma", preRegistrationShort: "Pre-iscrizione",
    registration: "Iscrizione", artists: "Portatori di Luce", youthMeetings: "Incontri europei dei giovani",
    becomeVolunteer: "Diventa volontario", bookletFeasts: "Passages — Il Libretto delle Feste",
    usefulLinks: "Buono a sapersi", contacts: "Contatti",
    passeurs: "Passeurs della Baia",
    passeursFoi: "Passeurs dell'Indicibile",
    passeursHistoireSciences: "Passeurs di storia e scienze",
    partners: "Partner",
    gallery: "le Prieuré s'éveille…",
  },
};

translations.es = {
  locale: "es",
  common: { switchTo: "EN", readMore: "Leer más", back: "Volver", loading: "Cargando…",
    submit: "Enviar", support: "Apoyar", join: "Adherirse", donate: "Donar", bookTickets: "Reservar",
    free: "Entrada libre", euro: "€" },
  nav: { home: "Inicio", documents: "Documentos", events: "Eventos", offices: "Oficios", concerts: "Conciertos", conferences: "Conferencias", autres: "Otros", agenda: "Agenda", presence: "Fiestas de San Leonardo",
    association: "Asociación", mecenat: "Mecenazgo", contact: "Contacto", admin: "Admin" },
  home: {
    kicker: "Vains · Bahía del Mont-Saint-Michel",
    heroTitle: "Priorato de San Leonardo",
    heroSubtitle: "Sobre un promontorio dominando la bahía, desde los Merovingios…",
    heroTagline: "Donde la experiencia sensible de lo Profano se encuentra con lo Sagrado.",
    introTitle: "El alma de los lugares está impregnada de paz, escucha y libertad interior.",
    introBody: [
      "Entre cielo y arenales, última piedra antes del mar y primera promesa del Monte,",
      "En el cruce de la Vía de los Duques y la Vía Angélica proveniente del Monte Sant'Angelo, voces sagradas se elevan,",
      "Del Levante al Poniente, como el curso de la vida,",
      "Desde Oriente, en el priorato, san Leonardo nos libera de todas nuestras prisiones antes de la travesía de las aguas de la Muerte hacia la Jerusalén Celestial,",
      "Hacia el Occidente, en el Monte Saint-Michel, el Arcángel pesa nuestras almas y nos pone en el camino de la Resurrección.",
    ],
    historyKicker: "Historia",
    historyTitle: "Del « León del Bosque » al renacimiento contemporáneo",
    historyChapters: [
      { title: "578 — Leodovaldo, futuro san Leonardo",
        body: "Nacido en 540 en una familia franca, cercano a Clodoveo, encargado de los prisioneros del reino de Francia, Leodovaldo — el « León del Bosque » — se convierte en el primer obispo de Avranches en 578. Termina su vida como ermitaño en Lemosín y da su nombre a esta tierra habitada desde la prehistoria. Su devoción se extiende por toda Europa; se convierte en el santo patrón de los prisioneros." },
      { title: "1087 — Donación de Guillermo el Conquistador",
        body: "Nacido en 1027 en Falaise y fallecido el 9 de septiembre de 1087 en Rouen, Guillermo el Conquistador dona a su muerte, en 1087, el dominio a los benedictinos de la abadía de Saint-Étienne de Caen, llamada « Abadía de los Hombres ». Los monjes erigen allí un priorato. El lugar se convierte en la última etapa de los peregrinos hacia el Mont Saint-Michel — atestiguado desde 1025." },
      { title: "Siglo XVII — El declive",
        body: "La encomienda marca el declive del priorato en el siglo XVII, la comunidad monástica abandona el lugar, mantenido en adelante por un agricultor local. Convertido en bien nacional durante la Revolución, un particular lo adquiere y lo transforma en explotación agrícola." },
      { title: "1944–1970 — Recuperación y clasificación",
        body: "Durante la Segunda Guerra Mundial, la Wehrmacht instala un observatorio en la cima del antiguo campanario. En junio de 1944, la familia Poilvillain de Misouard recupera el lugar respetando la donación del duque normando a los benedictinos de Caen. El priorato es clasificado Monumento Histórico en enero de 1970." },
      { title: "1975 → hoy — El renacimiento",
        body: "Desde 1975, surge el proyecto de hacer renacer y vivir este lugar en el espíritu de sus raíces. Las obras de restauración se lanzan en los años 80. La antigua granja es demolida, dando paso a la capilla. Transformado en vivienda, el campanario es restablecido. La estatua venerada de san Leonardo es restaurada." },
    ],
    prayerTitle: "Oración a san Leonardo",
    prayerLines: [
      "« San Leonardo, líbrame de todo vínculo malo y dame la fuerza de la Caridad. »",
      "« Concédenos ver los vínculos que nos encadenan y las relaciones que nos liberan. »",
    ],
    featuredArticleKicker: "Actualidad",
    featuredArticleTitle: "Un bautismo conmovedor",
    featuredArticleBody:
      "El 29 de agosto de 2024, la campana santa Ana — tras peregrinaciones entre la Marina francesa, Marruecos y Normandía — encontró su nombre y su vocación en el priorato.",
    eventsKicker: "Agenda", eventsTitle: "Próximos encuentros", eventsCta: "Ver todos los eventos",
    presenceKicker: "Millenium 2027", presenceTitle: "PRESENCE — Fiestas de San Leonardo",
    presenceBody: "Del 2 al 4 de julio de 2027 : tres días de Liberación, Luz y Unidad. Conferencias, ostensiones, procesión al Monte, hermanamiento con la abadía San Leonardo de Foggia.",
    presenceCta: "Descubrir e inscribirse",
    supportKicker: "Apoyar", supportTitle: "Haga vivir el Priorato",
    supportBody: "Adhiérase a los Amigos del Priorato o contribuya a los proyectos de restauración en curso. Cada gesto prolonga mil años de historia.",
  },
  documents: {
    kicker: "Archivos", title: "Documentos y recursos",
    intro: "Aquí encontrará un fondo documental, iconográfico, musical y vídeo para profundizar en el conocimiento del lugar y su ecosistema. Estamos abiertos a cualquier sugerencia pertinente.",
    categories: { all: "Todos", history: "Historia y Archivos", patrimony: "Patrimonio y Planos", association: "Vida de la Asociación", millenium: "Fiestas Millenium 2027" },
    featured: "Destacados", searchPlaceholder: "Buscar un documento…", empty: "Aún no hay documentos en esta categoría.",
    open: "Consultar", download: "Descargar", downloads: "vistas", downloadsOne: "vista",
  },
  events: { kicker: "Agenda", title: "Próximos eventos", empty: "No hay eventos por el momento.",
    details: "Detalles y reserva", ticketedTag: "Reserva obligatoria", freeTag: "Entrada libre", atTime: "a las" },
  eventsByCategory: {
    office: { kicker: "Vida litúrgica", title: "Próximos oficios", empty: "Ningún oficio programado por ahora." },
    concert: { kicker: "Música", title: "Próximos conciertos", empty: "Ningún concierto programado por ahora." },
    conference: { kicker: "Encuentros", title: "Próximas conferencias", empty: "Ninguna conferencia programada por ahora." },
    autre: { kicker: "Agenda", title: "Otros eventos", empty: "Ningún otro evento por ahora." },
  },
  eventDetail: { bookTitle: "Reserve sus plazas", quantity: "Número de plazas", fullName: "Nombre completo",
    email: "Correo electrónico", total: "Total", payNow: "Pagar en línea", free: "Entrada libre — sin reserva necesaria." },
  association: {
    kicker: "Únase a nosotros", title: "Los Amigos del Priorato",
    mission: "La Asociación « Los Amigos del Priorato de San Leonardo » tiene por objeto :",
    missions: [
      "Promover el patrimonio cultural, artístico y espiritual del Priorato y su entorno, mediante eventos e iniciativas conformes al espíritu del lugar y abiertas al público más amplio;",
      "Desarrollar investigaciones científicas y estudios vinculados a este lugar;",
      "Contribuir al mantenimiento de la capilla y sus alrededores.",
    ],
    membershipNote: "La adhesión es válida durante 12 meses a partir de la fecha de suscripción. Los habitantes de Vains se benefician de la tarifa reducida.",
    tiersTitle: "Cuotas de adhesión",
    tierLabels: { plein: "Tarifa completa", reduit: "Tarifa reducida", reduitNote: "artistas, residentes de Vains, desempleados, estudiantes, jubilados",
      couple: "Pareja", coupleNote: "dos miembros del mismo hogar",
      scouts_guides: "Scouts y Guías", scoutsGuidesNote: "una sola membresía por patrulla, equipo o clan",
      libre: "Donación libre", libreNote: "Importe a su elección" },
    formTitle: "Sus datos", fullName: "Nombre completo", email: "Correo electrónico",
    freeAmount: "Importe (€)", proceedJoin: "Continuar con la adhesión", proceedDonate: "Continuar con la donación",
  },
  contact: { kicker: "Escríbanos", title: "Contacto",
    intro: "¿Una pregunta, una sugerencia, una visita o una propuesta de colaboración? Escríbanos — respondemos lo antes posible.",
    name: "Nombre completo", email: "Correo electrónico", phone: "Teléfono (opcional)",
    subject: "Asunto", message: "Mensaje", send: "Enviar mensaje",
    success: "Gracias, su mensaje ha sido enviado.", direct: "También puede contactarnos directamente:",
    phoneLabel: "Teléfono", addressLabel: "Dirección" },
  payment: { checking: "Verificando el pago…", successTitle: "Pago confirmado",
    successBody: "Gracias por su apoyo. Recibirá una confirmación por correo en breve.",
    pendingTitle: "Pago en proceso", pendingBody: "Estamos comprobando el estado de su transacción.",
    failedTitle: "Pago no realizado", failedBody: "El pago no pudo ser confirmado. Puede intentarlo de nuevo.",
    backHome: "Volver al inicio" },
  presence: {
    kicker: "Millenium 2027 · Año europeo de los Normandos", title: "PRESENCE",
    tagline: "Donde la experiencia sensible de lo Profano se encuentra con lo Sagrado.",
    intro: "PRESENCE es el proyecto artístico, patrimonial y cultural emblemático del Priorato de San Leonardo, dentro del programa Millenium 2027 dirigido por la Región Normandía. Reactiva la memoria de la donación de 1087 de Guillermo el Conquistador, celebra a san Leonardo y teje cooperaciones europeas en torno a las rutas normandas.",
    pillarsTitle: "Cuatro pilares",
    pillars: [
      { title: "Frescos medievales", body: "Frescos inspirados en las técnicas del siglo XII : el Pantocrátor, el dominio ducal de Vains, la donación de 1087, san Benito patrón de Europa, san Leonardo, Bohemundo de Antioquía." },
      { title: "Ostensiones contemporáneas", body: "Eventos públicos que revisitan la tradición septenal de las ostensiones de san Leonardo — patrimonio UNESCO — abiertos a antiguos prisioneros y peregrinos." },
      { title: "Mediación & talleres", body: "Conferencias académicas, encuentros con artistas e investigadores, talleres de fresco, icono, canto, paseos botánicos medievales." },
      { title: "Encuentros europeos", body: "Encuentros y cooperación con la abadía de San Leonardo de Siponto (Foggia, Italia) ; reconexión de la red europea de lugares normandos (Sicilia, Inglaterra, Italia del Norte)." },
    ],
    eventTitle: "Fiestas populares de San Leonardo — del 1 de julio a las 17 h al 5 de julio a las 11 h de 2027",
    eventSubtitle: "Tres días, tres temas : Liberación · Luz · Unidad",
    fresquesKicker: "Creación artística", fresquesTitle: "Los frescos, corazón del proyecto",
    fresquesBody: "Inspirados en los pigmentos hallados en la capilla y en las técnicas románicas del siglo XII, los frescos de PRESENCE inscriben el relato de manera duradera en la piedra. Concebidos con artistas e investigadores franceses e italianos, abren un diálogo entre el arte medieval y la sensibilidad contemporánea.",
    fresquesQuote: "« Inscribir la historia en la piedra. »",
    fresquesStudies: "Estudios · 2025—2027", fresquesCta: "Descubrir la creación",
    fresquesThemes: ["El Pantocrátor", "El dominio ducal de Vains", "La donación de 1087", "San Benito, patrón de Europa", "San Leonardo", "Bohemundo I de Antioquía"],
    unescoBadge: "Patrimonio UNESCO", unescoTitle: "Las Ostensiones de San Leonardo",
    unescoBody: "Las Ostensiones septenales — tradición viva desde el siglo XI — fueron inscritas en 2013 en la lista UNESCO del Patrimonio Cultural Inmaterial de la Humanidad. Cada siete años, las reliquias de san Leonardo salen del priorato para ser veneradas por peregrinos, antiguos prisioneros y fieles de toda Europa. Las Fiestas 2027 marcan la próxima gran Ostensión.",
    preRegKicker: "Sin compromiso", preRegTitle: "Pre-inscripción — sin compromiso",
    preRegBody: "Manifieste su interés desde ahora para las Fiestas de San Leonardo. Le volveremos a contactar con las modalidades definitivas y los detalles prácticos. Ningún pago requerido en esta fase.",
    contactBlock: "Para cualquier pregunta sobre PRESENCE:", contactName: "Auriane de Potesta",
  },
  footer: {
    tagline: "En la vía de los peregrinos, entre la ruta de los Duques y la Vía Angélica.",
    contact: "Contacto", address: "Prieuré saint Léonard, 50300 Vains, France",
    navTitle: "Sitio", legal: "© 2026 Amigos del Priorato de San Leonardo. Asociación de interés general (Ley 1901).",
    legalNotice: "Aviso legal", privacy: "Privacidad",
  },
  ui: {
    preRegistration: "PRE-INSCRIPCIÓN", openMenu: "Abrir el menú", close: "Cerrar",
    mySpace: "Mi espacio", logout: "Cerrar sesión", signIn: "ACCEDER", register: "REGISTRARSE",
    backToHome: "Volver al inicio", friendsOfPriory: "Amigos del Priorato", resources: "Biblioteca",
    newsletterUnsubscribeTitle: "Cancelación", newsletterNoEmail: "No se proporcionó ningún correo electrónico. Verifique el enlace desde su bandeja de entrada.",
    newsletterBusy: "Procesando…", newsletterOk: "✓ Cancelación confirmada",
    newsletterOkBody: "La dirección {email} no recibirá más comunicaciones. Puede volver a inscribirse en cualquier momento desde la página de las Fiestas 2027.",
    newsletterFail: "Error al cancelar la suscripción. Contáctenos para asistencia.",
    origin: "En los orígenes de las Fiestas", programme: "Programa", preRegistrationShort: "Pre-inscripción",
    registration: "Inscripción", artists: "Portadores de Luz", youthMeetings: "Encuentros europeos de la juventud",
    becomeVolunteer: "Convertirse en voluntario", bookletFeasts: "Passages — El Folleto de las Fiestas",
    usefulLinks: "Bueno saberlo", contacts: "Contactos",
    passeurs: "Mensajeros de la Bahía",
    passeursFoi: "Mensajeros de lo Indecible",
    passeursHistoireSciences: "Mensajeros de la historia y las ciencias",
    partners: "Socios",
    gallery: "le Prieuré s'éveille…",
  },
};

translations.de = {
  locale: "de",
  common: { switchTo: "EN", readMore: "Weiterlesen", back: "Zurück", loading: "Lädt…",
    submit: "Senden", support: "Unterstützen", join: "Beitreten", donate: "Spenden", bookTickets: "Buchen",
    free: "Freier Eintritt", euro: "€" },
  nav: { home: "Startseite", documents: "Dokumente", events: "Veranstaltungen", offices: "Gottesdienste", concerts: "Konzerte", conferences: "Vorträge", autres: "Sonstiges", agenda: "Kalender", presence: "Sankt-Leonhard-Feste",
    association: "Verein", mecenat: "Mäzenatentum", contact: "Kontakt", admin: "Admin" },
  home: {
    kicker: "Vains · Bucht des Mont-Saint-Michel",
    heroTitle: "Priorei Sankt Leonhard",
    heroSubtitle: "Auf einer Anhöhe über der Bucht, seit den Merowingern…",
    heroTagline: "Wo die sinnliche Erfahrung des Profanen das Heilige berührt.",
    introTitle: "Die Seele des Ortes ist von Frieden, Lauschen und innerer Freiheit durchdrungen.",
    introBody: [
      "Zwischen Himmel und Watt, letzter Stein vor dem Meer und erste Verheißung des Berges,",
      "Am Kreuzweg der Straße der Herzöge und der Via Angelica vom Monte Sant'Angelo erheben sich heilige Stimmen,",
      "Vom Aufgang bis zum Untergang, wie der Lauf des Lebens,",
      "Vom Osten her, im Priorat, befreit uns der heilige Leonhard aus all unseren Gefängnissen, bevor wir die Wasser des Todes durchqueren zum Himmlischen Jerusalem,",
      "Nach Westen hin, am Mont-Saint-Michel, wägt der Erzengel unsere Seelen und führt uns auf den Weg der Auferstehung.",
    ],
    historyKicker: "Geschichte",
    historyTitle: "Vom „Löwen des Waldes\" zur heutigen Wiedergeburt",
    historyChapters: [
      { title: "578 — Leodovald, der spätere heilige Leonhard",
        body: "Geboren 540 in einer fränkischen Familie, vertraut mit Chlodwig, betraut mit den Gefangenen des Frankenreichs, wird Leodovald — der „Löwe des Waldes\" — 578 erster Bischof von Avranches. Er beendet sein Leben als Eremit im Limousin und gibt seinen Namen diesem Land, das seit prähistorischer Zeit bewohnt wird. Seine Verehrung breitet sich in ganz Europa aus; er wird zum Schutzpatron der Gefangenen." },
      { title: "1087 — Schenkung Wilhelms des Eroberers",
        body: "Geboren 1027 in Falaise und gestorben am 9. September 1087 in Rouen, schenkt Wilhelm der Eroberer bei seinem Tod 1087 das Gut den Benediktinern der Abtei Saint-Étienne in Caen, genannt „Männerabtei\". Die Mönche errichten dort eine Priorei. Der Ort wird zur letzten Etappe der Pilger zum Mont Saint-Michel — bezeugt seit 1025." },
      { title: "17. Jahrhundert — Der Niedergang",
        body: "Die Kommende markiert den Niedergang der Priorei im 17. Jahrhundert. Die Mönchsgemeinschaft verlässt den Ort, der fortan von einem örtlichen Landwirt unterhalten wird. Während der Revolution zum Nationalgut erklärt, erwirbt ihn ein Privatmann und verwandelt ihn in einen Bauernhof." },
      { title: "1944–1970 — Übernahme und Denkmalschutz",
        body: "Während des Zweiten Weltkriegs richtet die Wehrmacht auf dem ehemaligen Glockenturm einen Beobachtungsposten ein. Im Juni 1944 übernimmt die Familie Poilvillain de Misouard den Ort und respektiert die Schenkung des normannischen Herzogs an die Benediktiner von Caen. Die Priorei wird im Januar 1970 als historisches Denkmal eingetragen." },
      { title: "1975 → heute — Die Wiedergeburt",
        body: "Ab 1975 entsteht das Projekt, diesen Ort im Geist seiner Wurzeln neu zu beleben. Die Restaurierungsarbeiten beginnen in den 1980er Jahren. Der alte Bauernhof wird abgerissen und macht der Kapelle Platz. Der zur Wohnung umgebaute Glockenturm wird wiederhergestellt. Die verehrte Statue des heiligen Leonhard wird restauriert." },
    ],
    prayerTitle: "Gebet zum heiligen Leonhard",
    prayerLines: [
      "„Heiliger Leonhard, befreie mich von jeder bösen Bindung und gib mir die Kraft der Liebe.\"",
      "„Lass uns die Bande sehen, die uns ketten, und die Beziehungen, die uns befreien.\"",
    ],
    featuredArticleKicker: "Aktuelles",
    featuredArticleTitle: "Eine bewegende Taufe",
    featuredArticleBody:
      "Am 29. August 2024 fand die Glocke Sankt Anna — nach Wanderungen zwischen der französischen Marine, Marokko und der Normandie — in der Priorei ihren Namen und ihre Berufung.",
    eventsKicker: "Kalender", eventsTitle: "Kommende Begegnungen", eventsCta: "Alle Veranstaltungen sehen",
    presenceKicker: "Millenium 2027", presenceTitle: "PRESENCE — Fest des heiligen Leonhard",
    presenceBody: "Vom 2. bis 4. Juli 2027: drei Tage Befreiung, Licht und Einheit. Vorträge, Ostensionen, Prozession zum Mont, Partnerschaft mit der Abtei San Leonardo in Foggia.",
    presenceCta: "Entdecken & anmelden",
    supportKicker: "Unterstützen", supportTitle: "Die Priorei am Leben erhalten",
    supportBody: "Werden Sie Mitglied der Freunde der Priorei oder unterstützen Sie die laufenden Restaurierungsprojekte. Jede Geste verlängert tausend Jahre Geschichte.",
  },
  documents: {
    kicker: "Archive", title: "Dokumente & Ressourcen",
    intro: "Hier finden Sie einen Dokumenten-, Bild-, Musik- und Videofundus, um die Kenntnis des Ortes und seines Umfelds zu vertiefen. Wir sind offen für jede sachdienliche Anregung.",
    categories: { all: "Alle", history: "Geschichte & Archive", patrimony: "Erbe & Pläne", association: "Vereinsleben", millenium: "Millenium-Feste 2027" },
    featured: "Hervorgehoben", searchPlaceholder: "Dokument suchen…", empty: "In dieser Kategorie noch keine Dokumente.",
    open: "Ansehen", download: "Herunterladen", downloads: "Ansichten", downloadsOne: "Ansicht",
  },
  events: { kicker: "Kalender", title: "Kommende Veranstaltungen", empty: "Noch keine Veranstaltungen.",
    details: "Details & Buchung", ticketedTag: "Anmeldung erforderlich", freeTag: "Freier Eintritt", atTime: "um" },
  eventsByCategory: {
    office: { kicker: "Liturgisches Leben", title: "Kommende Gottesdienste", empty: "Noch kein Gottesdienst geplant." },
    concert: { kicker: "Musik", title: "Kommende Konzerte", empty: "Noch kein Konzert geplant." },
    conference: { kicker: "Begegnungen", title: "Kommende Vorträge", empty: "Noch kein Vortrag geplant." },
    autre: { kicker: "Kalender", title: "Weitere Veranstaltungen", empty: "Noch keine weiteren Veranstaltungen." },
  },
  eventDetail: { bookTitle: "Buchen Sie Ihre Plätze", quantity: "Anzahl der Plätze", fullName: "Vollständiger Name",
    email: "E-Mail-Adresse", total: "Gesamtbetrag", payNow: "Online bezahlen", free: "Freier Eintritt — keine Buchung erforderlich." },
  association: {
    kicker: "Werden Sie Mitglied", title: "Die Freunde der Priorei",
    mission: "Der Verein „Die Freunde der Priorei Sankt Leonhard\" hat zum Ziel :",
    missions: [
      "Das kulturelle, künstlerische und spirituelle Erbe der Priorei und ihrer Umgebung durch Veranstaltungen und Initiativen zu fördern, die dem Geist des Ortes entsprechen und einem möglichst breiten Publikum offenstehen;",
      "Wissenschaftliche Forschung und Studien zu diesem Ort zu entwickeln;",
      "Zum Erhalt der Kapelle und ihrer Umgebung beizutragen.",
    ],
    membershipNote: "Die Mitgliedschaft gilt 12 Monate ab dem Datum des Beitritts. Einwohner von Vains profitieren vom ermäßigten Tarif.",
    tiersTitle: "Mitgliedsbeiträge",
    tierLabels: { plein: "Voller Tarif", reduit: "Ermäßigter Tarif", reduitNote: "Künstler, Einwohner von Vains, Arbeitssuchende, Studierende, Rentner",
      couple: "Paar", coupleNote: "zwei Mitglieder desselben Haushalts",
      scouts_guides: "Pfadfinder & Scouts", scoutsGuidesNote: "eine Mitgliedschaft pro Patrouille, Team oder Clan",
      libre: "Freie Spende", libreNote: "Betrag Ihrer Wahl" },
    formTitle: "Ihre Daten", fullName: "Vollständiger Name", email: "E-Mail-Adresse",
    freeAmount: "Betrag (€)", proceedJoin: "Mitgliedschaft fortsetzen", proceedDonate: "Spende fortsetzen",
  },
  contact: { kicker: "Schreiben Sie uns", title: "Kontakt",
    intro: "Eine Frage, ein Vorschlag, eine Besuchs- oder Partnerschaftsanfrage? Schreiben Sie uns — wir antworten so bald wie möglich.",
    name: "Vollständiger Name", email: "E-Mail-Adresse", phone: "Telefon (optional)",
    subject: "Betreff", message: "Nachricht", send: "Nachricht senden",
    success: "Danke — Ihre Nachricht wurde übermittelt.", direct: "Sie können uns auch direkt erreichen:",
    phoneLabel: "Telefon", addressLabel: "Adresse" },
  payment: { checking: "Zahlung wird überprüft…", successTitle: "Zahlung bestätigt",
    successBody: "Vielen Dank für Ihre Unterstützung. Eine Bestätigung wird Ihnen in Kürze per E-Mail zugesandt.",
    pendingTitle: "Zahlung in Bearbeitung", pendingBody: "Wir prüfen den Status Ihrer Transaktion.",
    failedTitle: "Zahlung nicht abgeschlossen", failedBody: "Die Zahlung konnte nicht bestätigt werden. Sie können es erneut versuchen.",
    backHome: "Zurück zur Startseite" },
  presence: {
    kicker: "Millenium 2027 · Europäisches Jahr der Normannen", title: "PRESENCE",
    tagline: "Wo die sinnliche Erfahrung des Profanen das Heilige berührt.",
    intro: "PRESENCE ist das künstlerische, kulturelle und Erbe-Leitprojekt der Priorei Sankt Leonhard im Rahmen des Programms Millenium 2027 der Region Normandie. Es reaktiviert die Erinnerung an die Schenkung Wilhelms des Eroberers von 1087, feiert den heiligen Leonhard und knüpft europäische Kooperationen rund um die normannischen Wege.",
    pillarsTitle: "Vier Säulen",
    pillars: [
      { title: "Mittelalterliche Fresken", body: "Fresken inspiriert von den Techniken des 12. Jahrhunderts: der Pantokrator, das herzogliche Gut Vains, die Schenkung von 1087, der heilige Benedikt — Patron Europas, der heilige Leonhard, Bohemund von Antiochia." },
      { title: "Zeitgenössische Ostensionen", body: "Öffentliche Veranstaltungen, die die siebenjährliche Tradition der Ostensionen des heiligen Leonhard — UNESCO-Erbe — neu aufgreifen, offen für ehemalige Gefangene und Pilger." },
      { title: "Vermittlung & Werkstätten", body: "Wissenschaftliche Konferenzen, Begegnungen mit Künstlern und Forschern, Werkstätten für Fresko, Ikone, Gesang, mittelalterliche botanische Spaziergänge." },
      { title: "Europäische Begegnungen", body: "Begegnungen und Zusammenarbeit mit der Abtei San Leonardo di Siponto (Foggia, Italien); Wiederverknüpfung des europäischen Netzes der normannischen Orte (Sizilien, England, Norditalien)." },
    ],
    eventTitle: "Volksfeste des heiligen Leonhard — vom 1. Juli 17 Uhr bis 5. Juli 11 Uhr 2027",
    eventSubtitle: "Drei Tage, drei Themen: Befreiung · Licht · Einheit",
    fresquesKicker: "Künstlerische Schöpfung", fresquesTitle: "Die Fresken, Herz des Projekts",
    fresquesBody: "Inspiriert von den in der Kapelle gefundenen Pigmenten und den romanischen Techniken des 12. Jahrhunderts schreiben die Fresken von PRESENCE die Erzählung dauerhaft in den Stein. Entworfen mit französischen und italienischen Künstlern und Forschern eröffnen sie einen Dialog zwischen mittelalterlicher Kunst und zeitgenössischer Empfindsamkeit.",
    fresquesQuote: "„Die Geschichte in den Stein schreiben.\"",
    fresquesStudies: "Studien · 2025—2027", fresquesCta: "Die Schöpfung entdecken",
    fresquesThemes: ["Der Pantokrator", "Das herzogliche Gut Vains", "Die Schenkung von 1087", "Der heilige Benedikt, Patron Europas", "Der heilige Leonhard", "Bohemund I. von Antiochia"],
    unescoBadge: "UNESCO-Erbe", unescoTitle: "Die Ostensionen des heiligen Leonhard",
    unescoBody: "Die siebenjährlichen Ostensionen — seit dem 11. Jahrhundert lebendige Tradition — wurden 2013 in die UNESCO-Liste des immateriellen Kulturerbes der Menschheit eingetragen. Alle sieben Jahre verlassen die Reliquien des heiligen Leonhard die Priorei, um von Pilgern, ehemaligen Gefangenen und Gläubigen aus ganz Europa verehrt zu werden. Die Feste 2027 markieren die nächste große Ostension.",
    preRegKicker: "Unverbindlich", preRegTitle: "Vor-Anmeldung — unverbindlich",
    preRegBody: "Bekunden Sie schon jetzt Ihr Interesse an den Sankt-Leonhard-Festen. Wir melden uns mit den endgültigen Modalitäten und praktischen Details. Keine Zahlung in diesem Stadium erforderlich.",
    contactBlock: "Für jede Frage zu PRESENCE:", contactName: "Auriane de Potesta",
  },
  footer: {
    tagline: "Auf dem Pilgerweg, an der Kreuzung der Herzogsroute und der Via Angelica.",
    contact: "Kontakt", address: "Prieuré saint Léonard, 50300 Vains, France",
    navTitle: "Seite", legal: "© 2026 Freunde der Priorei Sankt Leonhard. Gemeinnütziger Verein (Gesetz 1901).",
    legalNotice: "Impressum", privacy: "Datenschutz",
  },
  ui: {
    preRegistration: "VOR-ANMELDUNG", openMenu: "Menü öffnen", close: "Schließen",
    mySpace: "Mein Bereich", logout: "Abmelden", signIn: "ANMELDEN", register: "REGISTRIEREN",
    backToHome: "Zurück zur Startseite", friendsOfPriory: "Freunde der Priorei", resources: "Bibliothek",
    newsletterUnsubscribeTitle: "Abmeldung", newsletterNoEmail: "Keine E-Mail-Adresse angegeben. Bitte prüfen Sie den Link in Ihrem Postfach.",
    newsletterBusy: "Wird verarbeitet…", newsletterOk: "✓ Abmeldung bestätigt",
    newsletterOkBody: "Die Adresse {email} wird keine Mitteilungen mehr erhalten. Sie können sich jederzeit über die Seite der Feste 2027 erneut anmelden.",
    newsletterFail: "Fehler bei der Abmeldung. Bitte kontaktieren Sie uns für Unterstützung.",
    origin: "Zu den Ursprüngen der Feste", programme: "Programm", preRegistrationShort: "Vor-Anmeldung",
    registration: "Anmeldung", artists: "Vermittler des Lichts", youthMeetings: "Europäische Jugendbegegnungen",
    becomeVolunteer: "Freiwilliger werden", bookletFeasts: "Passages — Das Festheft",
    usefulLinks: "Gut zu wissen", contacts: "Kontakte",
    passeurs: "Vermittler der Bucht",
    passeursFoi: "Vermittler des Unaussprechlichen",
    passeursHistoireSciences: "Vermittler der Geschichte und Wissenschaften",
    partners: "Partner",
    gallery: "le Prieuré s'éveille…",
  },
};

translations.pt = {
  locale: "pt",
  common: { switchTo: "EN", readMore: "Ler mais", back: "Voltar", loading: "A carregar…",
    submit: "Enviar", support: "Apoiar", join: "Aderir", donate: "Doar", bookTickets: "Reservar",
    free: "Entrada livre", euro: "€" },
  nav: { home: "Início", documents: "Documentos", events: "Eventos", offices: "Ofícios", concerts: "Concertos", conferences: "Conferências", autres: "Outros", agenda: "Agenda", presence: "Festas de São Leonardo",
    association: "Associação", mecenat: "Mecenato", contact: "Contacto", admin: "Admin" },
  home: {
    kicker: "Vains · Baía do Monte Saint-Michel",
    heroTitle: "Priorado de São Leonardo",
    heroSubtitle: "Num promontório sobre a baía, desde os Merovíngios…",
    heroTagline: "Onde a experiência sensorial do Profano encontra o Sagrado.",
    introTitle: "A alma dos lugares está impregnada de paz, escuta e liberdade interior.",
    introBody: [
      "Entre o céu e as praias, última pedra antes do mar e primeira promessa do Monte,",
      "No cruzamento da Estrada dos Duques e da Via Angélica vinda do Monte Sant'Angelo, vozes sagradas se elevam,",
      "Do Levante ao Poente, como o curso da vida,",
      "Desde o Oriente, no priorado, são Leonardo liberta-nos de todas as nossas prisões antes da travessia das águas da Morte rumo à Jerusalém Celeste,",
      "Em direção ao Ocidente, no Monte Saint-Michel, o Arcanjo pesa as nossas almas e coloca-nos no caminho da Ressurreição.",
    ],
    historyKicker: "História",
    historyTitle: "Do « Leão da Floresta » ao renascimento contemporâneo",
    historyChapters: [
      { title: "578 — Leodovaldo, futuro São Leonardo",
        body: "Nascido em 540 numa família franca, próximo de Clóvis, encarregado dos prisioneiros do reino de França, Leodovaldo — o « Leão da Floresta » — torna-se primeiro bispo de Avranches em 578. Termina a sua vida como eremita no Limousin e dá o seu nome a esta terra habitada desde a pré-história. A sua devoção espalha-se por toda a Europa; torna-se o santo padroeiro dos prisioneiros." },
      { title: "1087 — Doação de Guilherme, o Conquistador",
        body: "Nascido em 1027 em Falaise e falecido a 9 de setembro de 1087 em Ruão, Guilherme, o Conquistador, doa à sua morte, em 1087, o domínio aos beneditinos da abadia de Saint-Étienne de Caen, dita « Abadia dos Homens ». Os monges erguem aí um priorado. O lugar torna-se a última etapa dos peregrinos rumo ao Monte Saint-Michel — atestado desde 1025." },
      { title: "Século XVII — O declínio",
        body: "A comenda marca o declínio do priorado no século XVII, a comunidade monástica abandona o lugar, doravante mantido por um agricultor local. Tornado bem nacional durante a Revolução, um particular adquire-o e transforma-o em exploração agrícola." },
      { title: "1944–1970 — Recuperação e classificação",
        body: "Durante a Segunda Guerra Mundial, a Wehrmacht instala um posto de observação no cimo do antigo campanário. Em junho de 1944, a família Poilvillain de Misouard retoma o lugar respeitando a doação do duque normando aos beneditinos de Caen. O priorado é classificado como Monumento Histórico em janeiro de 1970." },
      { title: "1975 → hoje — O renascimento",
        body: "A partir de 1975, nasce o projeto de fazer renascer e viver este lugar no espírito das suas raízes. As obras de restauro são lançadas nos anos 80. A antiga quinta é demolida, dando lugar à capela. Transformado em habitação, o campanário é restabelecido. A estátua venerada de São Leonardo é restaurada." },
    ],
    prayerTitle: "Oração a São Leonardo",
    prayerLines: [
      "« São Leonardo, liberta-me de toda a ligação má e dá-me a força da Caridade. »",
      "« Concede-nos ver os laços que nos acorrentam e as relações que nos libertam. »",
    ],
    featuredArticleKicker: "Atualidade",
    featuredArticleTitle: "Um batismo comovente",
    featuredArticleBody:
      "A 29 de agosto de 2024, o sino Santa Ana — após peregrinações entre a Marinha francesa, Marrocos e a Normandia — encontrou o seu nome e a sua vocação no priorado.",
    eventsKicker: "Agenda", eventsTitle: "Próximos encontros", eventsCta: "Ver todos os eventos",
    presenceKicker: "Millenium 2027", presenceTitle: "PRESENCE — Festas de São Leonardo",
    presenceBody: "De 2 a 4 de julho de 2027: três dias de Libertação, Luz e Unidade. Conferências, ostensões, procissão até ao Monte, geminação com a abadia San Leonardo de Foggia.",
    presenceCta: "Descobrir e inscrever-se",
    supportKicker: "Apoiar", supportTitle: "Faça viver o Priorado",
    supportBody: "Adira aos Amigos do Priorado ou contribua para os projetos de restauro em curso. Cada gesto prolonga mil anos de história.",
  },
  documents: {
    kicker: "Arquivos", title: "Documentos e recursos",
    intro: "Aqui encontrará um fundo documental, iconográfico, musical e em vídeo para aprofundar o conhecimento do lugar e do seu ecossistema. Estamos disponíveis para qualquer sugestão pertinente.",
    categories: { all: "Todos", history: "História e Arquivos", patrimony: "Património e Plantas", association: "Vida da Associação", millenium: "Festas Millenium 2027" },
    featured: "Em destaque", searchPlaceholder: "Pesquisar um documento…", empty: "Ainda não há documentos nesta categoria.",
    open: "Consultar", download: "Descarregar", downloads: "visualizações", downloadsOne: "visualização",
  },
  events: { kicker: "Agenda", title: "Próximos eventos", empty: "Sem eventos de momento.",
    details: "Detalhes e reserva", ticketedTag: "Reserva obrigatória", freeTag: "Entrada livre", atTime: "às" },
  eventsByCategory: {
    office: { kicker: "Vida litúrgica", title: "Próximos ofícios", empty: "Sem ofícios programados de momento." },
    concert: { kicker: "Música", title: "Próximos concertos", empty: "Sem concertos programados de momento." },
    conference: { kicker: "Encontros", title: "Próximas conferências", empty: "Sem conferências programadas de momento." },
    autre: { kicker: "Agenda", title: "Outros eventos", empty: "Sem outros eventos de momento." },
  },
  eventDetail: { bookTitle: "Reserve os seus lugares", quantity: "Número de lugares", fullName: "Nome completo",
    email: "Endereço de e-mail", total: "Total", payNow: "Pagar online", free: "Entrada livre — sem reserva necessária." },
  association: {
    kicker: "Junte-se a nós", title: "Os Amigos do Priorado",
    mission: "A Associação « Os Amigos do Priorado de São Leonardo » tem por objetivo:",
    missions: [
      "Promover o património cultural, artístico e espiritual do Priorado e do seu meio envolvente, através de eventos e iniciativas conformes ao espírito do lugar e abertas ao público mais amplo;",
      "Desenvolver investigações científicas e estudos ligados a este lugar;",
      "Contribuir para a manutenção da capela e dos seus arredores.",
    ],
    membershipNote: "A adesão é válida durante 12 meses a partir da data de subscrição. Os habitantes de Vains beneficiam da tarifa reduzida.",
    tiersTitle: "Quotas de adesão",
    tierLabels: { plein: "Tarifa plena", reduit: "Tarifa reduzida", reduitNote: "artistas, residentes de Vains, desempregados, estudantes, reformados",
      couple: "Casal", coupleNote: "dois aderentes do mesmo agregado familiar",
      scouts_guides: "Escuteiros & Guias", scoutsGuidesNote: "uma única adesão por patrulha, equipa ou clã",
      libre: "Donativo livre", libreNote: "Valor à sua escolha" },
    formTitle: "Os seus dados", fullName: "Nome completo", email: "Endereço de e-mail",
    freeAmount: "Valor (€)", proceedJoin: "Prosseguir com a adesão", proceedDonate: "Prosseguir com o donativo",
  },
  contact: { kicker: "Escreva-nos", title: "Contacto",
    intro: "Uma questão, uma sugestão, uma visita ou uma proposta de parceria? Escreva-nos — respondemos o mais rapidamente possível.",
    name: "Nome completo", email: "Endereço de e-mail", phone: "Telefone (opcional)",
    subject: "Assunto", message: "Mensagem", send: "Enviar mensagem",
    success: "Obrigado, a sua mensagem foi enviada.", direct: "Pode também contactar-nos diretamente:",
    phoneLabel: "Telefone", addressLabel: "Morada" },
  payment: { checking: "A verificar o pagamento…", successTitle: "Pagamento confirmado",
    successBody: "Obrigado pelo seu apoio. Receberá em breve uma confirmação por e-mail.",
    pendingTitle: "Pagamento em processamento", pendingBody: "Estamos a verificar o estado da sua transação.",
    failedTitle: "Pagamento não concluído", failedBody: "O pagamento não pôde ser confirmado. Pode tentar novamente.",
    backHome: "Voltar ao início" },
  presence: {
    kicker: "Millenium 2027 · Ano europeu dos Normandos", title: "PRESENCE",
    tagline: "Onde a experiência sensorial do Profano encontra o Sagrado.",
    intro: "PRESENCE é o projeto artístico, patrimonial e cultural emblemático do Priorado de São Leonardo, no âmbito do programa Millenium 2027 conduzido pela Região da Normandia. Reativa a memória da doação de 1087 de Guilherme, o Conquistador, celebra São Leonardo e tece cooperações europeias em torno das vias normandas.",
    pillarsTitle: "Quatro pilares",
    pillars: [
      { title: "Frescos medievais", body: "Frescos inspirados nas técnicas do século XII: o Pantocrator, o domínio ducal de Vains, a doação de 1087, São Bento padroeiro da Europa, São Leonardo, Boemundo de Antioquia." },
      { title: "Ostensões contemporâneas", body: "Eventos públicos que revisitam a tradição septenal das ostensões de São Leonardo — património UNESCO — abertos a antigos prisioneiros e peregrinos." },
      { title: "Mediação e oficinas", body: "Conferências académicas, encontros com artistas e investigadores, oficinas de fresco, ícone, canto, passeios botânicos medievais." },
      { title: "Encontros europeus", body: "Encontros e cooperação com a abadia de San Leonardo di Siponto (Foggia, Itália); religação da rede europeia dos lugares normandos (Sicília, Inglaterra, Norte de Itália)." },
    ],
    eventTitle: "Festas populares de São Leonardo — de 1 de julho às 17 h a 5 de julho às 11 h de 2027",
    eventSubtitle: "Três dias, três temas: Libertação · Luz · Unidade",
    fresquesKicker: "Criação artística", fresquesTitle: "Os frescos, coração do projeto",
    fresquesBody: "Inspirados nos pigmentos encontrados na capela e nas técnicas românicas do século XII, os frescos de PRESENCE inscrevem o relato de forma duradoura na pedra. Concebidos com artistas e investigadores franceses e italianos, abrem um diálogo entre a arte medieval e a sensibilidade contemporânea.",
    fresquesQuote: "« Inscrever a história na pedra. »",
    fresquesStudies: "Estudos · 2025—2027", fresquesCta: "Descobrir a criação",
    fresquesThemes: ["O Pantocrator", "O domínio ducal de Vains", "A doação de 1087", "São Bento, padroeiro da Europa", "São Leonardo", "Boemundo I de Antioquia"],
    unescoBadge: "Património UNESCO", unescoTitle: "As Ostensões de São Leonardo",
    unescoBody: "As Ostensões septenais — tradição viva desde o século XI — foram inscritas em 2013 na lista UNESCO do Património Cultural Imaterial da Humanidade. De sete em sete anos, as relíquias de São Leonardo saem do priorado para serem veneradas por peregrinos, antigos prisioneiros e fiéis de toda a Europa. As Festas de 2027 assinalam a próxima grande Ostensão.",
    preRegKicker: "Sem compromisso", preRegTitle: "Pré-inscrição — sem compromisso",
    preRegBody: "Manifeste desde já o seu interesse pelas Festas de São Leonardo. Voltaremos a contactá-lo com as modalidades definitivas e os detalhes práticos. Nenhum pagamento é exigido nesta fase.",
    contactBlock: "Para qualquer questão sobre PRESENCE:", contactName: "Auriane de Potesta",
  },
  footer: {
    tagline: "Na via dos peregrinos, no cruzamento da rota dos Duques e da Via Angelica.",
    contact: "Contacto", address: "Prieuré saint Léonard, 50300 Vains, France",
    navTitle: "Sítio", legal: "© 2026 Amigos do Priorado de São Leonardo. Associação de interesse geral (Lei 1901).",
    legalNotice: "Aviso legal", privacy: "Privacidade",
  },
  ui: {
    preRegistration: "PRÉ-INSCRIÇÃO", openMenu: "Abrir o menu", close: "Fechar",
    mySpace: "O meu espaço", logout: "Sair", signIn: "ENTRAR", register: "REGISTAR",
    backToHome: "Voltar ao início", friendsOfPriory: "Amigos do Priorado", resources: "Biblioteca",
    newsletterUnsubscribeTitle: "Cancelamento", newsletterNoEmail: "Nenhum e-mail fornecido. Verifique o link na sua caixa de correio.",
    newsletterBusy: "A processar…", newsletterOk: "✓ Cancelamento confirmado",
    newsletterOkBody: "O endereço {email} deixará de receber comunicações. Pode reinscrever-se a qualquer momento a partir da página das Festas 2027.",
    newsletterFail: "Erro ao cancelar a subscrição. Contacte-nos para obter assistência.",
    origin: "Nas origens das Festas", programme: "Programa", preRegistrationShort: "Pré-inscrição",
    registration: "Inscrição", artists: "Portadores de Luz", youthMeetings: "Encontros europeus dos jovens",
    becomeVolunteer: "Tornar-se voluntário", bookletFeasts: "Passages — O Folheto das Festas",
    usefulLinks: "Bom saber", contacts: "Contactos",
    passeurs: "Passeurs da Baía",
    passeursFoi: "Passeurs do Indizível",
    passeursHistoireSciences: "Passeurs da história e das ciências",
    partners: "Parceiros",
    gallery: "le Prieuré s'éveille…",
  },
};

// ============================================================================
// Chinese (Simplified / Mandarin — 简体中文) — minimal block (falls back to FR)
// ============================================================================
translations.zh = {
  locale: "zh",
  common: { switchTo: "EN", readMore: "阅读更多", back: "返回", loading: "加载中…",
    submit: "提交", support: "支持", join: "加入", donate: "捐赠", bookTickets: "预订",
    free: "免费入场", euro: "€" },
  nav: { home: "首页", documents: "文献", events: "活动", offices: "礼仪", concerts: "音乐会", conferences: "讲座", autres: "其他", agenda: "日程", presence: "圣莱奥纳尔节",
    association: "协会", mecenat: "赞助", contact: "联系", admin: "管理" },
  home: {
    kicker: "凡 · 圣米歇尔山湾",
    heroTitle: "圣莱奥纳尔修道院",
    heroSubtitle: "自梅罗文加时代以来,矗立于俯瞰海湾的山岬上……",
    heroTagline: "凡俗的感官体验与神圣相遇之处。",
    introTitle: "此地之魂浸润着和平、倾听与内心的自由。",
    introBody: [
      "在天与沙滩之间,临海最后一石,圣山最初承诺,",
      "公爵之路与来自圣安杰罗山的天使之路在此交汇,神圣之声升起,",
      "自东至西,如生命的轨迹,",
      "自东方,在修道院中,圣莱奥纳尔将我们从一切牢笼中释放,我们方能穿越死亡之水,前往天上的耶路撒冷,",
      "向着西方,在圣米歇尔山,大天使衡量我们的灵魂,引领我们走上复活之路。",
    ],
    eventsKicker: "日程", eventsTitle: "即将到来的活动", eventsCta: "查看所有活动",
    presenceKicker: "千禧 2027", presenceTitle: "PRESENCE — 圣莱奥纳尔节",
    presenceCta: "了解并报名",
    supportKicker: "支持", supportTitle: "助修道院延续",
  },
  footer: {
    tagline: "在朝圣者的道路上,公爵之路与天使之路的交汇处。",
    contact: "联系", address: "Prieuré saint Léonard, 50300 Vains, France",
    navTitle: "网站", legal: "© 2026 圣莱奥纳尔修道院之友。公益协会(1901 法案)。",
    legalNotice: "法律声明", privacy: "隐私政策",
  },
  ui: {
    preRegistration: "预先报名", openMenu: "打开菜单", close: "关闭",
    mySpace: "我的空间", logout: "退出", signIn: "登录", register: "注册",
    backToHome: "返回首页", friendsOfPriory: "修道院之友", resources: "图书馆",
    gallery: "le Prieuré s'éveille…", origin: "节日起源", programme: "节目", preRegistrationShort: "预报名",
    registration: "报名", artists: "光的引路人", youthMeetings: "欧洲青年聚会",
    becomeVolunteer: "成为志愿者", bookletFeasts: "Passages — 节日手册",
    usefulLinks: "实用信息", contacts: "联系方式",
    passeurs: "海湾的引路人", passeursFoi: "不可言喻者的引路人",
    passeursHistoireSciences: "历史与科学的引路人", partners: "合作伙伴",
  },
};

// ============================================================================
// Polish (Polski) — minimal block (falls back to FR)
// ============================================================================
translations.pl = {
  locale: "pl",
  common: { switchTo: "EN", readMore: "Czytaj więcej", back: "Wróć", loading: "Ładowanie…",
    submit: "Wyślij", support: "Wesprzyj", join: "Dołącz", donate: "Wpłać darowiznę", bookTickets: "Zarezerwuj",
    free: "Wstęp wolny", euro: "€" },
  nav: { home: "Strona główna", documents: "Dokumenty", events: "Wydarzenia",
    offices: "Nabożeństwa", concerts: "Koncerty", conferences: "Konferencje", autres: "Inne", agenda: "Kalendarz",
    presence: "Święto św. Leonarda", association: "Stowarzyszenie", mecenat: "Mecenat",
    contact: "Kontakt", admin: "Administracja" },
  home: {
    kicker: "Vains · Zatoka Mont-Saint-Michel",
    heroTitle: "Klasztor św. Leonarda",
    heroSubtitle: "Na przylądku nad zatoką, od czasów Merowingów…",
    heroTagline: "Tam, gdzie doświadczenie zmysłowe Profanum spotyka Sacrum.",
    introTitle: "Dusza tego miejsca jest przeniknięta pokojem, wsłuchaniem i wewnętrzną wolnością.",
    introBody: [
      "Między niebem a piaskami, ostatni kamień przed morzem i pierwsza obietnica Góry,",
      "Na skrzyżowaniu Drogi Książąt i Via Angelica z Monte Sant'Angelo wznoszą się święte głosy,",
      "Ze Wschodu na Zachód, jak bieg życia,",
      "Ze Wschodu, w klasztorze, święty Leonard wyzwala nas ze wszystkich naszych więzień przed przeprawą przez wody Śmierci ku Jerozolimie Niebiańskiej,",
      "Ku Zachodowi, na Mont-Saint-Michel, Archanioł waży nasze dusze i wprowadza nas na drogę Zmartwychwstania.",
    ],
    eventsKicker: "Kalendarz", eventsTitle: "Nadchodzące spotkania", eventsCta: "Zobacz wszystkie",
    presenceKicker: "Millenium 2027", presenceTitle: "PRESENCE — Święto św. Leonarda",
    presenceCta: "Odkryj i zapisz się",
    supportKicker: "Wspieraj", supportTitle: "Daj życie Klasztorowi",
  },
  footer: {
    tagline: "Na drodze pielgrzymów, na skrzyżowaniu Drogi Książąt i Via Angelica.",
    contact: "Kontakt", address: "Prieuré saint Léonard, 50300 Vains, Francja",
    navTitle: "Strona", legal: "© 2026 Przyjaciele Klasztoru św. Leonarda. Stowarzyszenie pożytku publicznego (Ustawa 1901).",
    legalNotice: "Nota prawna", privacy: "Prywatność",
  },
  ui: {
    preRegistration: "PRE-REJESTRACJA", openMenu: "Otwórz menu", close: "Zamknij",
    mySpace: "Moja strefa", logout: "Wyloguj", signIn: "ZALOGUJ", register: "ZAREJESTRUJ",
    backToHome: "Powrót do strony głównej", friendsOfPriory: "Przyjaciele Klasztoru", resources: "Biblioteka",
    gallery: "le Prieuré s'éveille…", origin: "U źródeł Święta", programme: "Program", preRegistrationShort: "Pre-rejestracja",
    registration: "Rejestracja", artists: "Przekaziciele Światła", youthMeetings: "Europejskie spotkania młodzieży",
    becomeVolunteer: "Zostań wolontariuszem", bookletFeasts: "Passages — Broszura Święta",
    usefulLinks: "Warto wiedzieć", contacts: "Kontakty",
    passeurs: "Przekaziciele Zatoki", passeursFoi: "Przekaziciele Niewysłowionego",
    passeursHistoireSciences: "Przekaziciele historii i nauk", partners: "Partnerzy",
  },
};


// ============================================================================
// Russian (Русский) — minimal block (falls back to FR for non-translated keys)
// ============================================================================
translations.ru = {
  locale: "ru",
  common: { switchTo: "EN", readMore: "Подробнее", back: "Назад", loading: "Загрузка…",
    submit: "Отправить", support: "Поддержать", join: "Присоединиться", donate: "Пожертвовать",
    bookTickets: "Забронировать", free: "Свободный вход", euro: "€" },
  nav: { home: "Главная", documents: "Документы", events: "События",
    offices: "Богослужения", concerts: "Концерты", conferences: "Конференции", autres: "Другое", agenda: "Календарь",
    presence: "Праздник святого Леонарда", association: "Ассоциация", mecenat: "Меценатство",
    contact: "Контакты", admin: "Администрация" },
  home: {
    kicker: "Ванс · Бухта Мон-Сен-Мишель",
    heroTitle: "Приорат святого Леонарда",
    heroSubtitle: "На мысе, возвышающемся над бухтой, со времён Меровингов…",
    heroTagline: "Там, где чувственный опыт мирского встречается со священным.",
    introTitle: "Душа этих мест проникнута миром, слушанием и внутренней свободой.",
    introBody: [
      "Меж небом и песками — последний камень перед морем и первое обещание Горы,",
      "На перекрёстке Пути герцогов и Виа Анжелика, идущей от Монте-Сант-Анджело, восходят священные голоса,",
      "От Востока до Запада, как ход жизни,",
      "С Востока, в приорстве, святой Леонард освобождает нас из всех наших темниц перед переходом через воды Смерти к Небесному Иерусалиму,",
      "К Западу, на Мон-Сен-Мишель, Архангел взвешивает наши души и наставляет нас на путь Воскресения.",
    ],
    eventsKicker: "Календарь", eventsTitle: "Предстоящие встречи", eventsCta: "Все события",
    presenceKicker: "Тысячелетие 2027", presenceTitle: "PRESENCE — Праздник святого Леонарда",
    presenceCta: "Узнать и записаться",
    supportKicker: "Поддержите", supportTitle: "Сохраним Приорат",
  },
  footer: {
    tagline: "На пути паломников, на пересечении Пути герцогов и Виа Анжелика.",
    contact: "Контакты", address: "Prieuré saint Léonard, 50300 Vains, Франция",
    navTitle: "Сайт", legal: "© 2026 Друзья Приората святого Леонарда. Общественно-полезная ассоциация (Закон 1901 года).",
    legalNotice: "Правовая информация", privacy: "Конфиденциальность",
  },
  ui: {
    preRegistration: "ПРЕДВАРИТЕЛЬНАЯ ЗАПИСЬ", openMenu: "Открыть меню", close: "Закрыть",
    mySpace: "Моё пространство", logout: "Выйти", signIn: "ВОЙТИ", register: "РЕГИСТРАЦИЯ",
    backToHome: "На главную", friendsOfPriory: "Друзья Приората", resources: "Библиотека",
    gallery: "le Prieuré s'éveille…", origin: "Истоки праздника", programme: "Программа",
    preRegistrationShort: "Запись", registration: "Регистрация",
    artists: "Носители света", youthMeetings: "Европейские молодёжные встречи",
    becomeVolunteer: "Стать волонтёром", bookletFeasts: "Passages — Брошюра праздника",
    usefulLinks: "Полезная информация", contacts: "Контакты",
    passeurs: "Носители бухты", passeursFoi: "Носители Невыразимого",
    passeursHistoireSciences: "Носители истории и наук", partners: "Партнёры",
  },
};


// ============================================================================
// Ukrainian (Українська) — minimal block (falls back to FR for non-translated keys)
// ============================================================================
translations.uk = {
  locale: "uk",
  common: { switchTo: "EN", readMore: "Дізнатися більше", back: "Назад", loading: "Завантаження…",
    submit: "Надіслати", support: "Підтримати", join: "Приєднатися", donate: "Пожертвувати",
    bookTickets: "Бронювати", free: "Вільний вхід", euro: "€" },
  nav: { home: "Головна", documents: "Документи", events: "Події",
    offices: "Богослужіння", concerts: "Концерти", conferences: "Конференції", autres: "Інше", agenda: "Календар",
    presence: "Свято святого Леонарда", association: "Об'єднання", mecenat: "Меценатство",
    contact: "Контакти", admin: "Адміністрація" },
  home: {
    kicker: "Ван · Затока Мон-Сен-Мішель",
    heroTitle: "Пріорство святого Леонарда",
    heroSubtitle: "На мисі над затокою, з часів Меровінгів…",
    heroTagline: "Там, де чуттєвий досвід світського зустрічає Священне.",
    introTitle: "Душа цих місць пройнята миром, слуханням і внутрішньою свободою.",
    introBody: [
      "Між небом і пісками — останній камінь перед морем і перша обітниця Гори,",
      "На перехресті Шляху герцогів і Віа Анжеліка, що йде від Монте-Сант-Анджело, здіймаються священні голоси,",
      "Від Сходу до Заходу, як хід життя,",
      "Зі Сходу, у пріорстві, святий Леонард звільняє нас з усіх наших в'язниць перед переправою через води Смерті до Небесного Єрусалима,",
      "На Захід, на Мон-Сен-Мішель, Архангел зважує наші душі і наставляє нас на шлях Воскресіння.",
    ],
    eventsKicker: "Календар", eventsTitle: "Найближчі зустрічі", eventsCta: "Усі події",
    presenceKicker: "Тисячоліття 2027", presenceTitle: "PRESENCE — Свято святого Леонарда",
    presenceCta: "Дізнатися та записатися",
    supportKicker: "Підтримайте", supportTitle: "Збережімо Пріорство",
  },
  footer: {
    tagline: "На шляху паломників, на перехресті Шляху герцогів і Віа Анжеліка.",
    contact: "Контакти", address: "Prieuré saint Léonard, 50300 Vains, Франція",
    navTitle: "Сайт", legal: "© 2026 Друзі Пріорства святого Леонарда. Громадська корисна асоціація (Закон 1901 року).",
    legalNotice: "Правова інформація", privacy: "Конфіденційність",
  },
  ui: {
    preRegistration: "ПОПЕРЕДНІЙ ЗАПИС", openMenu: "Відкрити меню", close: "Закрити",
    mySpace: "Мій простір", logout: "Вийти", signIn: "УВІЙТИ", register: "РЕЄСТРАЦІЯ",
    backToHome: "На головну", friendsOfPriory: "Друзі Пріорства", resources: "Бібліотека",
    gallery: "le Prieuré s'éveille…", origin: "Витоки свята", programme: "Програма",
    preRegistrationShort: "Запис", registration: "Реєстрація",
    artists: "Носії світла", youthMeetings: "Європейські молодіжні зустрічі",
    becomeVolunteer: "Стати волонтером", bookletFeasts: "Passages — Брошура свята",
    usefulLinks: "Корисна інформація", contacts: "Контакти",
    passeurs: "Носії затоки", passeursFoi: "Носії Невимовного",
    passeursHistoireSciences: "Носії історії та наук", partners: "Партнери",
  },
};


// ============================================================================
// Romanian (Română) — minimal block (falls back to FR for non-translated keys)
// ============================================================================
translations.ro = {
  locale: "ro",
  common: { switchTo: "EN", readMore: "Citește mai mult", back: "Înapoi", loading: "Se încarcă…",
    submit: "Trimite", support: "Sprijină", join: "Alătură-te", donate: "Donează",
    bookTickets: "Rezervă", free: "Intrare liberă", euro: "€" },
  nav: { home: "Acasă", documents: "Documente", events: "Evenimente",
    offices: "Slujbe", concerts: "Concerte", conferences: "Conferințe", autres: "Altele", agenda: "Agendă",
    presence: "Sărbătoarea sfântului Leonard", association: "Asociația", mecenat: "Mecenat",
    contact: "Contact", admin: "Administrație" },
  home: {
    kicker: "Vains · Golful Mont-Saint-Michel",
    heroTitle: "Mănăstirea sfântului Leonard",
    heroSubtitle: "Pe un promontoriu deasupra golfului, încă din vremea merovingienilor…",
    heroTagline: "Acolo unde experiența senzorială a lumii întâlnește Sacrul.",
    introTitle: "Sufletul acestor locuri este pătruns de pace, ascultare și libertate interioară.",
    introBody: [
      "Între cer și nisipuri, ultima piatră înainte de mare și prima făgăduință a Muntelui,",
      "La răscrucea Drumului Ducilor și a Viei Angelica venind de la Monte Sant'Angelo, glasuri sacre se înalță,",
      "De la Răsărit la Apus, precum cursul vieții,",
      "Din Orient, la mănăstire, sfântul Leonard ne eliberează din toate temnițele noastre înainte de trecerea apelor Morții către Ierusalimul Ceresc,",
      "Către Occident, la Mont-Saint-Michel, Arhanghelul cumpănește sufletele noastre și ne așază pe calea Învierii.",
    ],
    eventsKicker: "Calendar", eventsTitle: "Întâlnirile viitoare", eventsCta: "Toate evenimentele",
    presenceKicker: "Mileniul 2027", presenceTitle: "PRESENCE — Sărbătoarea sfântului Leonard",
    presenceCta: "Află și înscrie-te",
    supportKicker: "Susține-ne", supportTitle: "Să salvăm Mănăstirea",
  },
  footer: {
    tagline: "Pe drumul pelerinilor, la răscrucea Drumului Ducilor și a Viei Angelica.",
    contact: "Contact", address: "Prieuré saint Léonard, 50300 Vains, Franța",
    navTitle: "Site", legal: "© 2026 Prietenii Mănăstirii sfântului Leonard. Asociație de utilitate publică (Legea 1901).",
    legalNotice: "Mențiuni legale", privacy: "Confidențialitate",
  },
  ui: {
    preRegistration: "PREÎNSCRIERE", openMenu: "Deschide meniul", close: "Închide",
    mySpace: "Spațiul meu", logout: "Deconectare", signIn: "AUTENTIFICARE", register: "ÎNSCRIERE",
    backToHome: "Înapoi acasă", friendsOfPriory: "Prietenii Mănăstirii", resources: "Bibliotecă",
    gallery: "le Prieuré s'éveille…", origin: "Originile sărbătorii", programme: "Program",
    preRegistrationShort: "Preînscriere", registration: "Înscriere",
    artists: "Purtătorii Luminii", youthMeetings: "Întâlnirile europene de tineret",
    becomeVolunteer: "Devino voluntar", bookletFeasts: "Passages — Broșura sărbătorii",
    usefulLinks: "Informații utile", contacts: "Contacte",
    passeurs: "Purtătorii Golfului", passeursFoi: "Purtătorii Nespusului",
    passeursHistoireSciences: "Purtătorii istoriei și științelor", partners: "Parteneri",
  },
};

// ============================================================================
// Swedish (Svenska) — minimal block (falls back to FR for non-translated keys)
// ============================================================================
translations.sv = {
  locale: "sv",
  common: { switchTo: "EN", readMore: "Läs mer", back: "Tillbaka", loading: "Laddar…",
    submit: "Skicka", support: "Stöd", join: "Gå med", donate: "Donera",
    bookTickets: "Boka", free: "Fritt inträde", euro: "€" },
  nav: { home: "Hem", documents: "Dokument", events: "Evenemang",
    offices: "Gudstjänster", concerts: "Konserter", conferences: "Föreläsningar", autres: "Övrigt", agenda: "Kalender",
    presence: "Sankt Leonardus fester", association: "Föreningen", mecenat: "Mecenat",
    contact: "Kontakt", admin: "Administration" },
  home: {
    kicker: "Vains · Mont-Saint-Michels bukt",
    heroTitle: "Sankt Leonardus priorat",
    heroSubtitle: "På ett näs över bukten, sedan merovingernas tid…",
    heroTagline: "Där sinnesupplevelsen av världen möter det Heliga.",
    introTitle: "Själen på denna plats är genomsyrad av frid, lyssnande och inre frihet.",
    introBody: [
      "Mellan himmel och sand, den sista stenen före havet och Bergets första löfte,",
      "Vid korsvägen mellan Hertigarnas väg och Via Angelica från Monte Sant'Angelo, stiger heliga röster,",
      "Från Öst till Väst, som livets gång,",
      "Från Orienten, vid priorat, befriar Sankt Leonardus oss från alla våra fängelser innan vi går över Dödens vatten till det Himmelska Jerusalem,",
      "Mot Väst, vid Mont-Saint-Michel, väger Ärkeängeln våra själar och sätter oss på Uppståndelsens väg.",
    ],
    eventsKicker: "Kalender", eventsTitle: "Kommande möten", eventsCta: "Alla evenemang",
    presenceKicker: "Millennium 2027", presenceTitle: "PRESENCE — Sankt Leonardus fester",
    presenceCta: "Upptäck & anmäl dig",
    vivaKicker: "VIVA ARTIST · Produktionslabel",
    vivaTitle: "Ett projekt att anförtro?",
    vivaBody:
      "Musik, sång, film, bild, installation — VIVA ARTIST är priorats label. Det välkomnar konstnärer som mejslar ljus och andedräkt för att få oss att darra av evighet.",
    vivaCta: "Upptäck labeln",
    supportKicker: "Stöd oss", supportTitle: "Håll priorat vid liv",
  },
  footer: {
    tagline: "På pilgrimernas väg, vid korsvägen mellan Hertigarnas väg och Via Angelica.",
    contact: "Kontakt", address: "Prieuré saint Léonard, 50300 Vains, Frankrike",
    navTitle: "Webbplats", legal: "© 2026 Vännerna till Sankt Leonardus priorat. Förening av allmännytta (lagen från 1901).",
    legalNotice: "Rättsligt meddelande", privacy: "Integritet",
  },
  ui: {
    preRegistration: "FÖRANMÄLAN", openMenu: "Öppna menyn", close: "Stäng",
    mySpace: "Mitt utrymme", logout: "Logga ut", signIn: "LOGGA IN", register: "REGISTRERA",
    backToHome: "Tillbaka till startsidan", friendsOfPriory: "Priorats Vänner", resources: "Bibliotek",
    origin: "Festens ursprung", programme: "Program",
    preRegistrationShort: "Föranmälan", registration: "Anmälan",
    artists: "Ljusets Bärare", youthMeetings: "Europeiska ungdomsmöten",
    becomeVolunteer: "Bli volontär", bookletFeasts: "Passages — Festhäftet",
    usefulLinks: "Bra att veta", contacts: "Kontakter",
    passeurs: "Buktens Färjekarlar", passeursFoi: "Det Outsagdas Bärare",
    passeursHistoireSciences: "Historiens och vetenskapernas bärare", partners: "Partner",
  },
};

// ============================================================================
// Norwegian Bokmål (Norsk) — minimal block (falls back to FR for non-translated keys)
// ============================================================================
translations.nb = {
  locale: "nb",
  common: { switchTo: "EN", readMore: "Les mer", back: "Tilbake", loading: "Laster…",
    submit: "Send", support: "Støtt", join: "Bli med", donate: "Gi",
    bookTickets: "Bestill", free: "Fri inngang", euro: "€" },
  nav: { home: "Hjem", documents: "Dokumenter", events: "Arrangementer",
    offices: "Gudstjenester", concerts: "Konserter", conferences: "Foredrag", autres: "Annet", agenda: "Kalender",
    presence: "Sankt Leonards fester", association: "Foreningen", mecenat: "Mesenat",
    contact: "Kontakt", admin: "Administrasjon" },
  home: {
    kicker: "Vains · Mont-Saint-Michel-bukta",
    heroTitle: "Sankt Leonards priorat",
    heroSubtitle: "På et nes over bukta, siden merovingernes tid…",
    heroTagline: "Der sanseerfaringen av verden møter det Hellige.",
    introTitle: "Sjelen på dette stedet er gjennomtrengt av fred, lytting og indre frihet.",
    introBody: [
      "Mellom himmel og sand, den siste steinen før havet og Fjellets første løfte,",
      "Ved korsveien mellom Hertugenes vei og Via Angelica fra Monte Sant'Angelo, stiger hellige røster,",
      "Fra Øst til Vest, som livets gang,",
      "Fra Orienten, ved priorat, frigjør Sankt Leonard oss fra alle våre fengsler før overgangen over Dødens vann til det Himmelske Jerusalem,",
      "Mot Vest, ved Mont-Saint-Michel, veier Erkeengelen sjelene våre og setter oss på Oppstandelsens vei.",
    ],
    eventsKicker: "Kalender", eventsTitle: "Kommende samlinger", eventsCta: "Alle arrangementer",
    presenceKicker: "Millennium 2027", presenceTitle: "PRESENCE — Sankt Leonards fester",
    presenceCta: "Oppdag & meld deg på",
    vivaKicker: "VIVA ARTIST · Produksjonslabel",
    vivaTitle: "Et prosjekt å betro?",
    vivaBody:
      "Musikk, sang, film, bilde, installasjon — VIVA ARTIST er priorats label. Den ønsker velkommen kunstnere som skjærer ut lys og pust for å få oss til å skjelve av evighet.",
    vivaCta: "Oppdag labelen",
    supportKicker: "Støtt oss", supportTitle: "Hold priorat i live",
  },
  footer: {
    tagline: "På pilegrimenes vei, i korsveien mellom Hertugenes vei og Via Angelica.",
    contact: "Kontakt", address: "Prieuré saint Léonard, 50300 Vains, Frankrike",
    navTitle: "Nettsted", legal: "© 2026 Vennene til Sankt Leonards priorat. Almennyttig forening (loven av 1901).",
    legalNotice: "Rettslig informasjon", privacy: "Personvern",
  },
  ui: {
    preRegistration: "FORHÅNDSPÅMELDING", openMenu: "Åpne menyen", close: "Lukk",
    mySpace: "Mitt område", logout: "Logg ut", signIn: "LOGG INN", register: "REGISTRER",
    backToHome: "Tilbake til forsiden", friendsOfPriory: "Priorats venner", resources: "Bibliotek",
    origin: "Festens opprinnelse", programme: "Program",
    preRegistrationShort: "Forhåndspåmelding", registration: "Påmelding",
    artists: "Lysets Bærere", youthMeetings: "Europeiske ungdomsmøter",
    becomeVolunteer: "Bli frivillig", bookletFeasts: "Passages — Festheftet",
    usefulLinks: "Godt å vite", contacts: "Kontakter",
    passeurs: "Buktas Ferjefolk", passeursFoi: "Det Usagtes Bærere",
    passeursHistoireSciences: "Historiens og vitenskapenes bærere", partners: "Partnere",
  },
};
